import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Palette, Eye, Upload, Trash2, Image as ImageIcon,
  Globe, Shield, ExternalLink, Info, CheckCircle, AlertCircle, ShoppingCart,
  LayoutTemplate, GripVertical, Plus, ChevronUp, ChevronDown, EyeOff, Save, Pencil,
  AlertTriangle,
} from 'lucide-react';
import { DomainRequestModal } from '@/components/DomainRequestModal';
import { RichTextEditor } from '@/components/RichTextEditor';
import { normalizeSafeUrl, sanitizeHtml } from '@/lib/sanitizeHtml';
import { BUILDER_EMOJI_GROUPS } from '@/lib/emoji';
import StorefrontSection from '@/components/StorefrontSection';
import {
  applySharedStorefrontContent,
  DEFAULT_SHARED_STOREFRONT_CONTENT,
  readSharedStorefrontContent,
  SHARED_STOREFRONT_CONTENT_KEY,
} from '@/lib/whiteLabelSharedContent';
import {
  BUILDER_FONTS,
  BRANDING_THEME_STORAGE_KEY,
  readStoredBrandingTheme,
  BUILDER_LIBRARY,
  getDefaultBuilderCards,
  getConfiguredDefaultSectionTypes,
  getAdminTemplateSections,
  getAdminTemplateCatalog,
  applyResellerContentOverrides,
  readResellerContentOverrides,
  createCompleteTemplatePage,
  getConfiguredSectionLibrary,
  applyAdminSectionAvailability,
  readAdminBuilderSettings,
  getMissingSectionsForAudit,
} from '@/lib/whiteLabelBuilderLib';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


const TABS = [
  { key: 'branding', label: 'Branding', icon: Palette },
  { key: 'domain', label: 'Domain Setup', icon: Globe },
  { key: 'ssl', label: 'SSL Setup', icon: Shield },
  { key: 'builder', label: 'Ready-made sites', icon: LayoutTemplate },
];


export default function ResellerWhiteLabel() {
  const requestedBuilderEdit = new URLSearchParams(window.location.search).get('edit') === '1';
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() =>
    new URLSearchParams(window.location.search).get('tab') === 'builder'
      ? 'builder'
      : 'branding'
  );
  const builderRootRef = useRef(null);
  const canvasPointerDragRef = useRef(null);
  const [selectedCanvasItem, setSelectedCanvasItem] = useState(null);
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [emojiPickerCategory, setEmojiPickerCategory] = useState('Smileys');
  const [emojiSearch, setEmojiSearch] = useState('');
  const [emojiInsertTarget, setEmojiInsertTarget] = useState('canvas');
  const [canvasRichTextEdit, setCanvasRichTextEdit] = useState(null);

  // ── Branding state ────────────────────────────────────────────────────────
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    primary_color: '#0f172a',
    accent_color: '#3b82f6',
    background_color: '#f8fafc',
    logo_url: '',
    show_pricing: true,
    company_name: '',
  });

  // ── Domain state ──────────────────────────────────────────────────────────
  const [domainOption, setDomainOption] = useState(null); // 'subdomain' | 'custom' | 'buy'
  const [domainInput, setDomainInput] = useState('');
  const [domainLoading, setDomainLoading] = useState(false);
  const [domainMsg, setDomainMsg] = useState(null);
  const [dnsRecords, setDnsRecords] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Domain Request Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingDomain, setPendingDomain] = useState('');
  const [pendingPrice, setPendingPrice] = useState(0);
  const [modalSubmitting, setModalSubmitting] = useState(false);

  // Page Builder state — preview and editing only
  const [selectedSectionId, setSelectedSectionId] = useState('sec-hero-page-1');
  const [activeTemplatePage, setActiveTemplatePage] = useState(0);
  const [templatePages, setTemplatePages] = useState({});
  const [resellerContentOverrides, setResellerContentOverrides] = useState({});
  const [showReadyMadeSites, setShowReadyMadeSites] = useState(!requestedBuilderEdit);
  const [selectedContentSectionId, setSelectedContentSectionId] = useState('');
  const [resellerContentSaved, setResellerContentSaved] = useState(true);
  const [pageSections, setPageSections] = useState(() => createCompleteTemplatePage(0));
  const [headingFontFamily, setHeadingFontFamily] = useState(BUILDER_FONTS[0].value);
  const [paragraphFontFamily, setParagraphFontFamily] = useState(BUILDER_FONTS[0].value);
  const [fontApplyTarget, setFontApplyTarget] = useState('both');
  const [fontSelection, setFontSelection] = useState(BUILDER_FONTS[0].value);
  const [builderLoading, setBuilderLoading] = useState(false);
  const [builderSaving, setBuilderSaving] = useState(false);
  const [published, setPublished] = useState(false);
  const [draggedSectionId, setDraggedSectionId] = useState(null);
  const [sharedContent, setSharedContent] = useState(readSharedStorefrontContent);
  const [showRightEditorInBuilder, setShowRightEditorInBuilder] = useState(
    () => readAdminBuilderSettings().showRightEditorInBuilder
  );
  // State-driven catalog so backend sync triggers re-render
  const [adminCatalog, setAdminCatalog] = useState(() => getAdminTemplateCatalog());
  // Publish audit: warns if admin template sections are missing from current page
  const [publishAuditState, setPublishAuditState] = useState({ open: false, missingSections: [] });

  useEffect(() => {
    // Load admin section library + page builder FIRST, then reseller settings.
    // Sequential order is critical: loadPageBuilderState (called inside fetchSettings)
    // reads from localStorage — if the section library isn't there yet, sections will
    // be empty. By awaiting syncAdminDesigns before fetchSettings we eliminate that
    // race condition.
    const init = async () => {
      // Step 1 — section library (source of truth for variants/types)
      try {
        const libResp = await axios.get(`${API}/admin/white-label/section-library`);
        const backendLib = libResp.data?.sections;
        if (Array.isArray(backendLib) && backendLib.length) {
          const libKey = 'cloudsetu_admin_section_library';
          const existingLib = JSON.parse(localStorage.getItem(libKey) || 'null');
          const libUpdatedAt = libResp.data?.updatedAt;
          const localUpdatedAt = existingLib?.[0]?._updatedAt;
          if (!Array.isArray(existingLib) || (libUpdatedAt && (!localUpdatedAt || libUpdatedAt > localUpdatedAt))) {
            localStorage.setItem(libKey, JSON.stringify(backendLib.map(s => ({ ...s, _updatedAt: libUpdatedAt }))));
          }
        }
      } catch { /* silent */ }

      // Step 2 — admin page builder designs (catalog)
      try {
        const { data } = await axios.get(`${API}/admin/white-label/page-builder`);
        if (data?.pages && Object.keys(data.pages).length) {
          const builderKey = 'cloudsetu_admin_page_builder';
          const existing = JSON.parse(localStorage.getItem(builderKey) || 'null');
          if (!existing?.pages || (data.updatedAt && (!existing.updatedAt || data.updatedAt > existing.updatedAt))) {
            localStorage.setItem(builderKey, JSON.stringify(data));
            // NOTE: Do NOT override cloudsetu_admin_section_library from page-builder's sectionLibrary
            // The section library is authoritative from /api/admin/white-label/section-library (Step 1 above).
            // The sectionLibrary field in page-builder may be stale/incomplete.
            setAdminCatalog(getAdminTemplateCatalog());
          }
        }
      } catch { /* silent */ }

      // Step 3 — reseller branding + page builder state (now has library available)
      fetchSettings();
    };
    init();
  }, []);

  useEffect(() => {
    const refreshBuilderSettings = (event) => {
      if (event.key && ![
        'cloudsetu_admin_section_library',
        SHARED_STOREFRONT_CONTENT_KEY,
        'cloudsetu_admin_settings',
      ].includes(event.key)) return;
      let adminLibrary = null;
      try {
        adminLibrary = JSON.parse(localStorage.getItem('cloudsetu_admin_section_library') || 'null');
      } catch {
        adminLibrary = null;
      }
      const nextContent = readSharedStorefrontContent();
      const tier = settings?.partner_tier || settings?.tier || 'Premium';
      setSharedContent(nextContent);
      setShowRightEditorInBuilder(readAdminBuilderSettings().showRightEditorInBuilder);
      setPageSections(previous => applyAdminSectionAvailability(
        applyResellerContentOverrides(applySharedStorefrontContent(previous, nextContent), resellerContentOverrides),
        adminLibrary,
        tier
      ));
      setTemplatePages(previous => Object.fromEntries(Object.entries(previous).map(([pageIndex, page]) => {
        const sections = Array.isArray(page?.sections) ? page.sections : Array.isArray(page) ? page : [];
        return [pageIndex, {
          ...(Array.isArray(page) ? {} : page),
          sections: applyResellerContentOverrides(applyAdminSectionAvailability(
            applySharedStorefrontContent(sections, nextContent),
            adminLibrary,
            tier
          ), resellerContentOverrides),
        }];
      })));
    };
    window.addEventListener('storage', refreshBuilderSettings);
    window.addEventListener('cloudsetu-admin-library-updated', refreshBuilderSettings);
    window.addEventListener('cloudsetu-shared-content-updated', refreshBuilderSettings);
    window.addEventListener('cloudsetu-admin-settings-updated', refreshBuilderSettings);
    return () => {
      window.removeEventListener('storage', refreshBuilderSettings);
      window.removeEventListener('cloudsetu-admin-library-updated', refreshBuilderSettings);
      window.removeEventListener('cloudsetu-shared-content-updated', refreshBuilderSettings);
      window.removeEventListener('cloudsetu-admin-settings-updated', refreshBuilderSettings);
    };
  }, [settings, resellerContentOverrides]);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    setActiveTab(tab === 'builder' ? 'builder' : 'branding');
  }, [location.search]);

  const normalizeLegacyPageSections = (sections, templateIndex) => {
    if (!Array.isArray(sections)) return createCompleteTemplatePage(templateIndex);
    if (sections.length <= BUILDER_LIBRARY.length * 2) return sections;
    return BUILDER_LIBRARY.map(librarySection => {
      const expectedVariant = librarySection.variants[templateIndex] || librarySection.variants[0];
      return sections.find(section => section.type === librarySection.type && section.variant === expectedVariant)
        || sections.find(section => section.type === librarySection.type);
    }).filter(Boolean);
  };

  const restorePageBuilderState = (saved, settingsValue = settings) => {
    const savedFont = BUILDER_FONTS.find(font => font.value === saved?.fontFamily)?.value || BUILDER_FONTS[0].value;
    const savedHeadingFont = BUILDER_FONTS.find(font => font.value === saved?.headingFontFamily)?.value || savedFont;
    const savedParagraphFont = BUILDER_FONTS.find(font => font.value === saved?.paragraphFontFamily)?.value || savedFont;
    setHeadingFontFamily(savedHeadingFont);
    setParagraphFontFamily(savedParagraphFont);
    setFontSelection(savedHeadingFont);
    setFontApplyTarget('both');
    const savedPages = saved?.pages && typeof saved.pages === 'object' ? { ...saved.pages } : {};
    if (saved?.sections && !savedPages[0]) {
      savedPages[0] = {
        sections: normalizeLegacyPageSections(saved.sections, 0),
        published: Boolean(saved.published),
      };
    }

    const catalog = getAdminTemplateCatalog();
    const availableIndices = catalog.map(template => template.index);
    const search = new URLSearchParams(window.location.search);
    const requestedPage = search.get('edit') === '1'
      ? Number(search.get('page'))
      : Number(saved?.activeTemplatePage);
    const pageIndex = availableIndices.includes(requestedPage) ? requestedPage : (availableIndices[0] ?? 0);
    const restoredSharedContent = readSharedStorefrontContent();
    const restoredOverrides = readResellerContentOverrides(saved);
    setSharedContent(restoredSharedContent);
    setResellerContentOverrides(restoredOverrides);
    let adminLibrary = null;
    try {
      adminLibrary = JSON.parse(localStorage.getItem('cloudsetu_admin_section_library') || 'null');
    } catch {
      adminLibrary = null;
    }
    const partnerTier = settingsValue?.partner_tier || settingsValue?.tier || 'Premium';
    setActiveTemplatePage(pageIndex);
    const restoredPages = Object.fromEntries(catalog.map(template => {
      const savedPage = savedPages[template.index];
      const sections = Array.isArray(savedPage?.sections)
        ? savedPage.sections
        : Array.isArray(savedPage)
          ? savedPage
          : createCompleteTemplatePage(template.index);
      return [template.index, {
        ...(Array.isArray(savedPage) ? {} : savedPage),
        name: template.name,
        sections: applyResellerContentOverrides(
          applyAdminSectionAvailability(
            applySharedStorefrontContent(sections, restoredSharedContent),
            adminLibrary,
            partnerTier
          ),
          restoredOverrides
        ),
      }];
    }));
    const activePage = restoredPages[pageIndex];
    const normalizedActiveSections = activePage?.sections || [];
    setTemplatePages(restoredPages);
    setPageSections(normalizedActiveSections);
    setSelectedSectionId(normalizedActiveSections[0]?.id || null);
    setPublished(Boolean(activePage?.published ?? (pageIndex === 0 && saved?.published)));
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/white-label/settings`);
      setSettings(response.data);
      setFormData({
        primary_color: response.data.primary_color || readStoredBrandingTheme().primary_color || '#0f172a',
        accent_color: response.data.accent_color || readStoredBrandingTheme().accent_color || '#3b82f6',
        background_color: readStoredBrandingTheme().background_color || '#f8fafc',
        logo_url: response.data.logo_url || '',
        show_pricing: response.data.show_pricing ?? true,
        company_name: response.data.company_name || '',
      });
      try {
        localStorage.setItem(BRANDING_THEME_STORAGE_KEY, JSON.stringify({
          primary_color: response.data.primary_color || readStoredBrandingTheme().primary_color || '#0f172a',
          accent_color: response.data.accent_color || readStoredBrandingTheme().accent_color || '#3b82f6',
          background_color: readStoredBrandingTheme().background_color || '#f8fafc',
          logo_url: response.data.logo_url || '',
          company_name: response.data.company_name || '',
        }));
      } catch (error) {
        console.error('Unable to cache backend branding for storefront preview.', error);
      }
      loadPageBuilderState(response.data);
    } catch {
      toast.error('Failed to fetch white-label settings');
      try {
        const stored = localStorage.getItem('cloudsetu_page_builder_partner');
        const saved = stored ? JSON.parse(stored) : null;
        restorePageBuilderState(saved);
      } catch {
        restorePageBuilderState(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Logo handlers ─────────────────────────────────────────────────────────
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) { toast.error('Invalid file type. Allowed: PNG, JPG, GIF, WEBP'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('File size must be less than 2MB'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await axios.post(`${API}/white-label/upload-logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo uploaded successfully!');
      setFormData(prev => ({ ...prev, logo_url: response.data.logo_url }));
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/white-label/delete-logo`);
      toast.success('Logo removed successfully');
      setFormData(prev => ({ ...prev, logo_url: '' }));
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete logo');
    }
  };

  const getLogoDisplayUrl = (logoUrl) => {
    if (!logoUrl) return null;
    return logoUrl.startsWith('/') ? `${BACKEND_URL}${logoUrl}` : logoUrl;
  };

  const handleSaveBranding = async (e) => {
    e.preventDefault();
    try {
      const { background_color, ...settingsPayload } = formData;
      await axios.put(`${API}/white-label/settings`, settingsPayload);
      localStorage.setItem(BRANDING_THEME_STORAGE_KEY, JSON.stringify({
        primary_color: formData.primary_color,
        accent_color: formData.accent_color,
        background_color,
        logo_url: formData.logo_url,
        company_name: formData.company_name,
      }));
      toast.success('White-label settings updated successfully');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    }
  };

  // ── Domain handlers ───────────────────────────────────────────────────────
  const resetDomainState = (key) => {
    setDomainOption(key);
    setDomainInput('');
    setDomainMsg(null);
    setDnsRecords(null);
    setSearchResults(null);
  };

  const handleSetSubdomain = async () => {
    if (!domainInput.trim()) return;
    setDomainLoading(true); setDomainMsg(null);
    try {
      const res = await axios.put(`${API}/white-label/domain/subdomain`, {
        subdomain: domainInput.trim().toLowerCase(),
      });
      setDomainMsg({ type: 'success', text: `Activated! Your storefront: ${res.data.url}` });
      fetchSettings();
    } catch (e) {
      setDomainMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to set subdomain' });
    } finally { setDomainLoading(false); }
  };

  const handleSetCustomDomain = async () => {
    if (!domainInput.trim()) return;
    setDomainLoading(true); setDomainMsg(null); setDnsRecords(null);
    try {
      const res = await axios.put(`${API}/white-label/domain/custom`, { domain: domainInput.trim() });
      setDnsRecords(res.data.dns_records);
      setDomainMsg({ type: 'info', text: res.data.message });
      fetchSettings();
    } catch (e) {
      setDomainMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to save domain' });
    } finally { setDomainLoading(false); }
  };

  const handleVerifyDNS = async () => {
    setVerifying(true); setDomainMsg(null);
    try {
      const res = await axios.post(`${API}/white-label/domain/verify`);
      if (res.data.verified) {
        setDomainMsg({ type: 'success', text: res.data.message });
        setDnsRecords(null);
        fetchSettings();
      } else {
        setDomainMsg({ type: 'error', text: res.data.message });
      }
    } catch (e) {
      setDomainMsg({ type: 'error', text: e.response?.data?.detail || 'Verification failed' });
    } finally { setVerifying(false); }
  };

  const handleSearchDomains = async () => {
    if (!domainInput.trim()) return;
    setSearching(true); setSearchResults(null); setDomainMsg(null);
    try {
      const res = await axios.get(`${API}/white-label/domain/search`, { params: { name: domainInput.trim() } });
      setSearchResults(res.data.domains || []);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const isNetworkError = !e.response || e.code === 'ERR_NETWORK';
      const msg = isNetworkError
        ? 'Domain search requires internet access to ResellerClub. This works on the live production domain.'
        : (detail || 'Domain search is unavailable right now. This feature works on the live production domain.');
      setDomainMsg({ type: 'error', text: msg });
    } finally { setSearching(false); }
  };

  const handleBuyDomain = (domain, price) => {
    setPendingDomain(domain);
    setPendingPrice(price || 0);
    setModalOpen(true);
  };

  const handleModalSubmit = async (payload) => {
    setModalSubmitting(true);
    try {
      await axios.post(`${API}/domain/request`, payload);
      setModalOpen(false);
      setSearchResults(null);
      setDomainMsg({
        type: 'success',
        text: "Your domain request has been submitted. You'll be notified once it's approved.",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request submission failed');
    } finally {
      setModalSubmitting(false);
    }
  };

  const presetColors = [
    { name: 'Slate Blue', primary: '#0f172a', accent: '#3b82f6', background: '#f8fafc' },
    { name: 'Forest Green', primary: '#064e3b', accent: '#10b981', background: '#f0fdf4' },
    { name: 'Purple', primary: '#581c87', accent: '#a855f7', background: '#faf5ff' },
    { name: 'Orange', primary: '#7c2d12', accent: '#f97316', background: '#fff7ed' },
    { name: 'Teal', primary: '#134e4a', accent: '#14b8a6', background: '#f0fdfa' },
  ];

  const selectedSection = pageSections.find(section => section.id === selectedSectionId) || pageSections[0];

  useEffect(() => {
    if (selectedSectionId && pageSections.some(section => section.id === selectedSectionId)) return;
    setSelectedSectionId(pageSections[0]?.id || null);
  }, [pageSections, selectedSectionId]);

  const getBuilderStorageKey = (settingsValue = settings) =>
    `cloudsetu_page_builder_${settingsValue?.id || settingsValue?.reseller_id || settingsValue?.subdomain || 'partner'}`;

  const loadPageBuilderState = (settingsValue = settings) => {
    if (!settingsValue) return;
    setBuilderLoading(true);
    try {
      const stored = localStorage.getItem(getBuilderStorageKey(settingsValue));
      const saved = stored ? JSON.parse(stored) : settingsValue?.page_builder;
      restorePageBuilderState(saved);
    } catch {
      restorePageBuilderState(null);
    } finally {
      setBuilderLoading(false);
    }
  };

  const changeTemplatePage = (pageIndex) => {
    if (!Number.isInteger(pageIndex) || !adminCatalog.some(template => template.index === pageIndex)
      || pageIndex === activeTemplatePage) return;
    const updatedPages = {
      ...templatePages,
      [activeTemplatePage]: { ...(templatePages[activeTemplatePage] || {}), sections: pageSections, published },
    };
    const nextPage = updatedPages[pageIndex];
    const nextSections = Array.isArray(nextPage?.sections)
      ? nextPage.sections
      : Array.isArray(nextPage)
        ? nextPage
        : createCompleteTemplatePage(pageIndex);

    setTemplatePages(updatedPages);
    setActiveTemplatePage(pageIndex);
    let adminLibrary = null;
    try {
      adminLibrary = JSON.parse(localStorage.getItem('cloudsetu_admin_section_library') || 'null');
    } catch {
      adminLibrary = null;
    }
    const partnerTier = settings?.partner_tier || settings?.tier || 'Premium';
    const normalizedSections = applyAdminSectionAvailability(
      applyResellerContentOverrides(applySharedStorefrontContent(nextSections, sharedContent), resellerContentOverrides),
      adminLibrary,
      partnerTier
    );
    setPageSections(normalizedSections);
    setSelectedSectionId(normalizedSections[0]?.id || null);
    setPublished(Boolean(nextPage?.published));
  };

  const updateSharedSectionContent = (type, updates) => {
    const nextOverrides = {
      ...resellerContentOverrides,
      [type]: { ...resellerContentOverrides[type], ...updates },
    };
    setResellerContentOverrides(nextOverrides);
    setPageSections(previous => applyResellerContentOverrides(
      applySharedStorefrontContent(previous, sharedContent),
      nextOverrides
    ));
    setTemplatePages(previous => Object.fromEntries(Object.entries(previous).map(([pageIndex, page]) => {
      const sections = Array.isArray(page?.sections) ? page.sections : Array.isArray(page) ? page : [];
      return [pageIndex, {
        ...(Array.isArray(page) ? {} : page),
        sections: applyResellerContentOverrides(
          applySharedStorefrontContent(sections, sharedContent),
          nextOverrides
        ),
      }];
    })));
    setResellerContentSaved(false);
    setPublished(false);
  };

  const updateSharedCardContent = (type, cardIndex, field, value, listIndex) => {
    const cards = [...(resellerContentOverrides[type]?.cards || sharedContent[type]?.cards || [])];
    if (!cards[cardIndex]) return;
    const nextField = Number.isInteger(listIndex) && Array.isArray(cards[cardIndex][field])
      ? cards[cardIndex][field].map((item, index) => index === listIndex ? value.replace(/^✓\s*/, '') : item)
      : value;
    cards[cardIndex] = { ...cards[cardIndex], [field]: nextField };
    updateSharedSectionContent(type, { cards });
  };

  const savePageBuilderState = async (publish = false) => {
    setBuilderSaving(true);
    const synchronizedSections = applyResellerContentOverrides(
      applySharedStorefrontContent(pageSections, sharedContent),
      resellerContentOverrides
    );
    const synchronizedPages = Object.fromEntries(Object.entries(templatePages).map(([pageIndex, page]) => {
      const sections = Array.isArray(page?.sections) ? page.sections : Array.isArray(page) ? page : [];
      return [pageIndex, {
        ...(Array.isArray(page) ? {} : page),
        sections: applyResellerContentOverrides(
          applySharedStorefrontContent(sections, sharedContent),
          resellerContentOverrides
        ),
        published: Boolean(page?.published),
      }];
    }));
    const pages = {
      ...synchronizedPages,
      [activeTemplatePage]: { ...(synchronizedPages[activeTemplatePage] || {}), sections: synchronizedSections, published: false },
    };
    const requestPages = {
      ...pages,
      [activeTemplatePage]: { ...(pages[activeTemplatePage] || {}), sections: synchronizedSections, published: publish },
    };
    const draftState = {
      sections: synchronizedSections,
      pages,
      activeTemplatePage,
      fontFamily: headingFontFamily,
      headingFontFamily,
      paragraphFontFamily,
      sharedContent,
      contentOverridesBySectionType: resellerContentOverrides,
      published: false,
      status: 'Draft',
      updatedAt: new Date().toISOString(),
    };
    const key = getBuilderStorageKey();
    const partnerRecord = {
      id: settings?.id || settings?.reseller_id || settings?.subdomain || 'partner',
      name: formData.company_name || settings?.company_name || 'Partner',
      url: settings?.custom_domain
        ? `${settings.custom_domain}/public/${settings.subdomain || ''}`
        : settings?.subdomain ? `${settings.subdomain}.cloudsetu.com` : '',
      status: 'Draft',
      updated: draftState.updatedAt.slice(0, 10),
      published: false,
    };
    let localDraftSaved = false;
    try {
      localStorage.setItem(key, JSON.stringify(draftState));
      localDraftSaved = true;
      const partnersKey = 'cloudsetu_partner_pages';
      const existing = JSON.parse(localStorage.getItem(partnersKey) || '[]');
      const nextPartners = [partnerRecord, ...existing.filter(partner => partner.id !== partnerRecord.id)];
      localStorage.setItem(partnersKey, JSON.stringify(nextPartners));
      window.dispatchEvent(new Event('cloudsetu-page-builder-updated'));
      await axios[publish ? 'post' : 'put'](
        `${API}/white-label/page-builder/${publish ? 'publish' : 'draft'}`,
        {
          sections: synchronizedSections,
          pages: requestPages,
          activeTemplatePage,
          headingFontFamily,
          paragraphFontFamily,
          sharedContent,
          contentOverridesBySectionType: resellerContentOverrides,
          published: publish,
        }
      );
      if (publish) {
        const publishedState = {
          ...draftState,
          pages: requestPages,
          published: true,
          status: 'Published',
          updatedAt: new Date().toISOString(),
        };
        localStorage.setItem(key, JSON.stringify(publishedState));
        localStorage.setItem(partnersKey, JSON.stringify([{
          ...partnerRecord,
          status: 'Published',
          published: true,
          updated: publishedState.updatedAt.slice(0, 10),
        }, ...existing.filter(partner => partner.id !== partnerRecord.id)]));
        setTemplatePages(publishedState.pages);
        setPublished(true);
      } else {
        setTemplatePages(pages);
        setPublished(false);
      }
      toast.success(publish ? 'Storefront published successfully' : 'Draft saved successfully');
    } catch (error) {
      toast.error(
        `${publish ? 'Publish' : 'Draft sync'} failed. ${localDraftSaved ? 'Your draft is saved locally.' : 'The local draft could not be saved.'} ${
          error.response?.data?.detail || error.message || 'The server could not be reached.'
        }`
      );
      setPublished(false);
    } finally {
      setBuilderSaving(false);
    }
  };

  /**
   * Checks for missing sections vs the admin template before publishing.
   * Shows an audit warning dialog if any sections are missing; otherwise publishes directly.
   */
  const handlePublishClick = async () => {
    if (!saveResellerContent()) return;
    const checkSections = applyResellerContentOverrides(
      applySharedStorefrontContent(pageSections, sharedContent),
      resellerContentOverrides
    );
    console.log('[AUDIT DEBUG] checkSections types:', checkSections.map(s => s.type));
    console.log('[AUDIT DEBUG] activeTemplatePage:', activeTemplatePage);
    const missingSections = getMissingSectionsForAudit(checkSections, activeTemplatePage);
    console.log('[AUDIT DEBUG] missingSections:', missingSections);
    if (missingSections.length > 0) {
      console.log('[AUDIT DEBUG] Setting publishAuditState open=true');
      setPublishAuditState({ open: true, missingSections });
      console.log('[AUDIT DEBUG] setPublishAuditState called, returning');
      return;
    }
    await savePageBuilderState(true);
  };

  const addSectionToPage = (librarySection, selectedVariant) => {
    const id = `${librarySection.type}-${Date.now()}`;
    const newSection = {
      ...librarySection,
      id,
      name: librarySection.name,
      variant: selectedVariant || librarySection.defaultVariant,
      visible: true,
      heading: librarySection.heading || '',
      subtext: librarySection.subtext || '',
      primaryText: librarySection.primaryText || '',
      cards: getDefaultBuilderCards(librarySection.type),
    };
    setPageSections(previous => [...previous, ...applySharedStorefrontContent([newSection], sharedContent)]);
    setSelectedSectionId(id);
    toast.success(`${librarySection.name} added to page`);
  };

  const deleteSection = (id) => {
    setPageSections(previous => {
      const next = previous.filter(section => section.id !== id);
      if (selectedSectionId === id) setSelectedSectionId(next[0]?.id || null);
      return next;
    });
  };

  const getAccessibleSectionLibrary = () => {
    let adminLibrary = null;
    try {
      adminLibrary = JSON.parse(localStorage.getItem('cloudsetu_admin_section_library') || 'null');
    } catch {
      adminLibrary = null;
    }
    const partnerTier = settings?.partner_tier || settings?.tier || 'Premium';
    return getConfiguredSectionLibrary(adminLibrary, partnerTier)
      .filter(section => section.enabled && section.tierAllowed && section.variants.length);
  };

  const openStorefrontPreview = () => {
    const previewPath = `/storefront/preview?source=reseller&builderKey=${encodeURIComponent(getBuilderStorageKey())}&page=${activeTemplatePage}`;
    navigate(previewPath);
  };

  const updateSelectedSection = (field, value) => {
    const selected = pageSections.find(section => section.id === selectedSectionId);
    if (selected && ['heading', 'subtext', 'primaryText', 'cards'].includes(field)) {
      updateSharedSectionContent(selected.type, { [field]: value });
      return;
    }
    setPageSections(previous => previous.map(section =>
      section.id === selectedSectionId
        ? value === 'global'
          ? Object.fromEntries(Object.entries(section).filter(([key]) => key !== field))
          : { ...section, [field]: value }
        : section
    ));
  };

  const addCanvasShape = (type) => {
    const section = pageSections.find(item => item.id === selectedSectionId) || pageSections[0];
    if (!section) return;
    const shape = {
      id: `shape-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type,
      x: 24 + (section.shapes?.length || 0) * 18,
      y: 70 + (section.shapes?.length || 0) * 18,
      width: type === 'line' ? 140 : 120,
      height: type === 'line' ? 4 : 80,
      color: '#3b82f6',
    };
    setPageSections(previous => previous.map(item =>
      item.id === section.id ? { ...item, shapes: [...(item.shapes || []), shape] } : item
    ));
    setSelectedShapeId(shape.id);
  };

  const addCanvasEmoji = (emoji) => {
    const section = pageSections.find(item => item.id === selectedSectionId) || pageSections[0];
    if (!section) {
      toast.error('Select a section before adding an emoji');
      return;
    }
    const shape = {
      id: `emoji-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: 'emoji',
      emoji,
      x: 24 + (section.shapes?.length || 0) * 18,
      y: 70 + (section.shapes?.length || 0) * 18,
      width: 64,
      height: 64,
      color: 'transparent',
    };
    setPageSections(previous => previous.map(item =>
      item.id === section.id ? { ...item, shapes: [...(item.shapes || []), shape] } : item
    ));
    setSelectedShapeId(shape.id);
  };

  const applyBuilderEmoji = (emoji) => {
    if (emojiInsertTarget === 'canvas') {
      addCanvasEmoji(emoji);
    } else if (selectedCanvasItem) {
      setPageSections(previous => previous.map(section => section.id === selectedCanvasItem.sectionId
        ? {
            ...section,
            inlineTextEditsByVariant: {
              ...(section.inlineTextEditsByVariant || {}),
              [section.variant]: {
                ...(section.inlineTextEditsByVariant?.[section.variant] || section.inlineTextEdits || {}),
                [selectedCanvasItem.key]: `${section.inlineTextEditsByVariant?.[section.variant]?.[selectedCanvasItem.key] ?? section.inlineTextEdits?.[selectedCanvasItem.key] ?? selectedCanvasItem.text ?? ''}${emoji}`,
              },
            },
          }
        : section
      ));
    } else {
      toast.error('Select a text item on the canvas first');
      return;
    }
    setEmojiPickerOpen(false);
    setEmojiSearch('');
  };

  const applyGlobalFont = (font) => {
    setFontSelection(font);
    if (fontApplyTarget === 'both' || fontApplyTarget === 'heading') setHeadingFontFamily(font);
    if (fontApplyTarget === 'both' || fontApplyTarget === 'paragraph') setParagraphFontFamily(font);
  };

  const moveSection = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= pageSections.length) return;
    setPageSections(previous => {
      const reordered = [...previous];
      [reordered[index], reordered[nextIndex]] = [reordered[nextIndex], reordered[index]];
      return reordered;
    });
  };

  const toggleSectionVisibility = (id) => {
    setPageSections(previous => previous.map(section =>
      section.id === id ? { ...section, visible: !section.visible } : section
    ));
  };

  useEffect(() => {
    const handlePointerMove = (event) => {
      const drag = canvasPointerDragRef.current;
      if (drag?.mode === 'shape-move' || drag?.mode === 'shape-resize') {
        const deltaX = event.clientX - drag.startX;
        const deltaY = event.clientY - drag.startY;
        setPageSections(previous => previous.map(section => section.id === drag.sectionId
          ? {
              ...section,
              shapes: (section.shapes || []).map(shape => shape.id === drag.shapeId
                ? drag.mode === 'shape-move'
                  ? { ...shape, x: Math.max(0, drag.startLeft + deltaX), y: Math.max(0, drag.startTop + deltaY) }
                  : { ...shape, width: Math.max(20, drag.startWidth + deltaX), height: shape.type === 'line' ? shape.height : Math.max(20, drag.startHeight + deltaY) }
                : shape
              ),
            }
          : section
        ));
        return;
      }
      if (!drag || drag.mode === 'move') return;
      const horizontal = ['width', 'paddingX', 'marginX'].includes(drag.mode);
      const delta = horizontal ? event.clientX - drag.startX : event.clientY - drag.startY;
      const value = drag.mode === 'width'
        ? Math.max(25, Math.min(100, drag.startValue + (delta / drag.canvasWidth) * 100))
        : drag.mode === 'height'
          ? Math.max(80, Math.min(1200, drag.startValue + delta))
          : Math.max(0, Math.min(160, drag.startValue + delta));
      setPageSections(previous => previous.map(section =>
        section.id === drag.sectionId ? { ...section, [drag.mode]: Math.round(value) } : section
      ));
    };
    const handlePointerUp = (event) => {
      const drag = canvasPointerDragRef.current;
      if (drag?.mode === 'move') {
        const target = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-builder-section-id]');
        if (target && target.dataset.builderSectionId !== drag.sectionId) {
          setPageSections(previous => {
            const reordered = [...previous];
            const sourceIndex = reordered.findIndex(section => section.id === drag.sectionId);
            const targetIndex = reordered.findIndex(section => section.id === target.dataset.builderSectionId);
            if (sourceIndex < 0 || targetIndex < 0) return previous;
            const [moved] = reordered.splice(sourceIndex, 1);
            reordered.splice(targetIndex, 0, moved);
            return reordered;
          });
        }
        setSelectedSectionId(drag.sectionId);
        setDraggedSectionId(null);
      }
      canvasPointerDragRef.current = null;
    };
    const handlePointerCancel = () => {
      canvasPointerDragRef.current = null;
      setDraggedSectionId(null);
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerCancel);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerCancel);
    };
  }, []);

  useEffect(() => {
    if (activeTab !== 'builder') return undefined;
    const root = builderRootRef.current;
    if (!root) return undefined;

    const handleClick = (event) => {
      const shapeAction = event.target.closest('[data-shape-action]');
      if (shapeAction && root.contains(shapeAction)) {
        const sectionId = shapeAction.dataset.sectionId;
        const shapeId = shapeAction.dataset.shapeId;
        if (shapeAction.dataset.shapeAction === 'delete') {
          setPageSections(previous => previous.map(section => section.id === sectionId
            ? { ...section, shapes: (section.shapes || []).filter(shape => shape.id !== shapeId) }
            : section
          ));
          if (selectedShapeId === shapeId) setSelectedShapeId(null);
        } else {
          setSelectedSectionId(sectionId);
          setSelectedShapeId(shapeId);
        }
        return;
      }
      const emojiOption = event.target.closest('[data-builder-emoji]');
      if (emojiOption && root.contains(emojiOption)) {
        applyBuilderEmoji(emojiOption.dataset.builderEmoji);
        return;
      }
      const emojiMode = event.target.closest('[data-emoji-insert-target]');
      if (emojiMode && root.contains(emojiMode)) {
        setEmojiInsertTarget(emojiMode.dataset.emojiInsertTarget);
        return;
      }
      const shapeItem = event.target.closest('[data-shape-move]');
      if (shapeItem && root.contains(shapeItem) && !event.target.closest('input, button, [data-shape-resize]')) {
        setSelectedSectionId(shapeItem.dataset.sectionId);
        setSelectedShapeId(shapeItem.dataset.shapeId);
        return;
      }

      const content = event.target.closest('.builder-section-content');
      if (content && root.contains(content)) {
        const sectionElement = content.closest('[data-builder-section-id]');
        const canvasItem = event.target.closest('[data-canvas-item-key]');
        if (sectionElement && canvasItem) {
          setSelectedCanvasItem({
            sectionId: sectionElement.dataset.builderSectionId,
            key: canvasItem.dataset.canvasItemKey,
            text: canvasItem.textContent,
          });
          setSelectedSectionId(sectionElement.dataset.builderSectionId);
        }
      }

      const actionElement = event.target.closest('[data-builder-action]');
      if (!actionElement || !root.contains(actionElement)) return;
      const { builderAction: action, sectionId, builderSectionId, libraryType, variant, direction, shapeType } = actionElement.dataset;
      const resolvedSectionId = sectionId || builderSectionId;

      if (action === 'preview') openStorefrontPreview();
      if (action === 'save-draft') savePageBuilderState(false);
      if (action === 'publish') handlePublishClick();
      if (action === 'add') {
        const librarySection = getAccessibleSectionLibrary().find(item => item.type === libraryType);
        if (librarySection) addSectionToPage(librarySection, variant);
      }
      if (action === 'add-shape' && shapeType) addCanvasShape(shapeType);
      if (action === 'select' && resolvedSectionId) setSelectedSectionId(resolvedSectionId);
      if (action === 'delete' && resolvedSectionId) deleteSection(resolvedSectionId);
      if (action === 'visibility' && resolvedSectionId) toggleSectionVisibility(resolvedSectionId);
      if ((action === 'move-up' || action === 'move-down') && resolvedSectionId) {
        const index = pageSections.findIndex(section => section.id === resolvedSectionId);
        if (index >= 0) moveSection(index, action === 'move-up' ? -1 : 1);
      }
    };

    const handleDoubleClick = (event) => {
      const editable = event.target.closest('.builder-section-content [data-shared-field], .builder-section-content [data-shared-card-field]');
      if (!editable || !root.contains(editable)) return;
      event.preventDefault();
      const sectionElement = editable.closest('[data-builder-section-id]');
      const section = pageSections.find(item => item.id === sectionElement?.dataset.builderSectionId);
      if (!section) return;
      const cardIndex = Number(editable.dataset.sharedCardIndex);
      const isCardField = editable.dataset.sharedCardField !== undefined;
      const field = isCardField ? editable.dataset.sharedCardField : editable.dataset.sharedField;
      const listIndex = Number(editable.dataset.sharedListIndex);
      const cards = section.contentOverrides?.cards || section.cards || [];
      const currentValue = isCardField
        ? Number.isInteger(cardIndex)
          ? Number.isInteger(listIndex)
            ? cards[cardIndex]?.[field]?.[listIndex]
            : cards[cardIndex]?.[field]
          : ''
        : section.contentOverrides?.[field] ?? section[field] ?? '';
      setSelectedSectionId(section.id);
      setCanvasRichTextEdit({
        sectionId: section.id,
        field,
        cardIndex: Number.isInteger(cardIndex) ? cardIndex : null,
        listIndex: Number.isInteger(listIndex) ? listIndex : null,
        label: field.replace(/([A-Z])/g, ' $1'),
        value: String(currentValue ?? ''),
      });
    };

    const handleInput = (event) => {
      const field = event.target.dataset?.builderField;
      if (!field) return;
      updateSelectedSection(field, event.target.value);
    };

    const handleChange = (event) => {
      if (event.target.dataset?.builderShapeSelect !== undefined) {
        if (event.target.value) addCanvasShape(event.target.value);
        event.target.value = '';
        return;
      }
      const shapeId = event.target.dataset?.builderShapeColor;
      if (shapeId) {
        const sectionId = event.target.dataset.sectionId;
        setPageSections(previous => previous.map(section => section.id === sectionId
          ? { ...section, shapes: (section.shapes || []).map(shape => shape.id === shapeId ? { ...shape, color: event.target.value } : shape) }
          : section
        ));
        return;
      }
      if (event.target.dataset?.builderFontTarget !== undefined) {
        const target = event.target.value;
        setFontApplyTarget(target);
        setFontSelection(target === 'paragraph' ? paragraphFontFamily : headingFontFamily);
        return;
      }
      if (event.target.dataset?.builderFont !== undefined) {
        applyGlobalFont(event.target.value);
        return;
      }
      const preset = event.target.dataset?.builderPreset;
      if (preset !== undefined) {
        changeTemplatePage(Number(event.target.value));
        return;
      }
      const field = event.target.dataset?.builderField;
      if (!field) return;
      if (event.target.type === 'checkbox') {
        updateSelectedSection(field, event.target.checked);
      } else {
        updateSelectedSection(field, event.target.value);
      }
    };

    const handleCanvasPointerDown = (event) => {
      const shapeResize = event.target.closest('[data-shape-resize]');
      const shapeMove = event.target.closest('[data-shape-move]');
      if ((shapeResize || shapeMove) && root.contains(shapeResize || shapeMove)) {
        if (!shapeResize && event.target.closest('input, button')) return;
        event.preventDefault();
        event.stopPropagation();
        const target = shapeResize || shapeMove;
        const sectionId = target.dataset.sectionId;
        const shapeId = target.dataset.shapeId;
        const shape = pageSections.find(item => item.id === sectionId)?.shapes?.find(item => item.id === shapeId);
        if (!shape) return;
        setSelectedSectionId(sectionId);
        setSelectedShapeId(shapeId);
        canvasPointerDragRef.current = {
          sectionId,
          shapeId,
          mode: shapeResize ? 'shape-resize' : 'shape-move',
          startX: event.clientX,
          startY: event.clientY,
          startLeft: Number(shape.x || 0),
          startTop: Number(shape.y || 0),
          startWidth: Number(shape.width || 120),
          startHeight: Number(shape.height || 80),
        };
        return;
      }
      const moveHandle = event.target.closest('[data-canvas-move]');
      const handle = event.target.closest('[data-canvas-adjust]');
      if (moveHandle && root.contains(moveHandle)) {
        event.preventDefault();
        event.stopPropagation();
        const sectionId = moveHandle.dataset.sectionId;
        const sectionElement = moveHandle.closest('[data-builder-section-id]');
        if (!sectionElement) return;
        setSelectedSectionId(sectionId);
        setDraggedSectionId(sectionId);
        canvasPointerDragRef.current = {
          sectionId,
          mode: 'move',
          startX: event.clientX,
          startY: event.clientY,
          startValue: 0,
          canvasWidth: sectionElement.getBoundingClientRect().width,
        };
        return;
      }
      if (!handle || !root.contains(handle)) return;
      event.preventDefault();
      event.stopPropagation();
      const sectionId = handle.dataset.sectionId;
      const section = pageSections.find(item => item.id === sectionId);
      const sectionElement = handle.closest('[data-builder-section-id]');
      if (!section || !sectionElement) return;
      const mode = handle.dataset.canvasAdjust;
      const defaults = {
        width: 100,
        height: 0,
        paddingX: 24,
        paddingY: section.type === 'hero' ? 40 : 32,
        marginX: 0,
        marginY: 0,
      };
      setSelectedSectionId(sectionId);
      canvasPointerDragRef.current = {
        sectionId,
        mode,
        startX: event.clientX,
        startY: event.clientY,
        startValue: Number(section[mode] ?? defaults[mode]),
        canvasWidth: sectionElement.parentElement?.getBoundingClientRect().width || sectionElement.getBoundingClientRect().width,
      };
    };

    root.addEventListener('click', handleClick);
    root.addEventListener('dblclick', handleDoubleClick);
    root.addEventListener('input', handleInput);
    root.addEventListener('change', handleChange);
    root.addEventListener('pointerdown', handleCanvasPointerDown);

    return () => {
      root.removeEventListener('click', handleClick);
      root.removeEventListener('dblclick', handleDoubleClick);
      root.removeEventListener('input', handleInput);
      root.removeEventListener('change', handleChange);
      root.removeEventListener('pointerdown', handleCanvasPointerDown);
    };
  }, [activeTab, selectedSectionId, pageSections, builderSaving, activeTemplatePage, templatePages, published, headingFontFamily, paragraphFontFamily, fontApplyTarget]);

  useEffect(() => {
    if (activeTab !== 'builder') return;
    const root = builderRootRef.current;
    if (!root) return;
    root.querySelectorAll('[data-builder-section-id]').forEach(sectionElement => {
      const section = pageSections.find(item => item.id === sectionElement.dataset.builderSectionId);
      const content = sectionElement.querySelector('.builder-section-content');
      if (!section || !content) return;
      let textKey = 0;
      Array.from(content.querySelectorAll('*')).forEach((element, index) => {
        element.dataset.canvasItemKey = String(index);
        const isSelected = selectedCanvasItem?.sectionId === section.id && selectedCanvasItem.key === String(index);
        if (isSelected) {
          element.dataset.canvasItemSelected = 'true';
          element.style.outline = '2px dotted #2563eb';
          element.style.outlineOffset = '2px';
        } else if (element.dataset.canvasItemSelected) {
          delete element.dataset.canvasItemSelected;
          element.style.removeProperty('outline');
          element.style.removeProperty('outline-offset');
        }
        if (!element.children.length && !['IMG', 'VIDEO', 'IFRAME', 'BR', 'HR', 'INPUT'].includes(element.tagName)) {
          const editKey = String(textKey++);
          element.dataset.inlineTextKey = editKey;
          const variantEdits = section.inlineTextEditsByVariant?.[section.variant] || section.inlineTextEdits || {};
          if (Object.prototype.hasOwnProperty.call(variantEdits, editKey)) {
            element.textContent = variantEdits[editKey];
          }
        }
      });
    });
  }, [activeTab, pageSections, selectedCanvasItem]);

  const isDomainLive =
    settings?.domain_status === 'active' || settings?.domain_status === 'verified';
  const isDomainPending = settings?.domain_status === 'pending_verification';

  const exitBuilder = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('tab');
    window.history.replaceState({}, '', url.toString());
    setShowReadyMadeSites(true);
    setActiveTab('branding');
  };

  const updateResellerSectionContent = (sectionId, field, value) => {
    const section = pageSections.find(item => item.id === sectionId);
    if (!section) return;
    updateSharedSectionContent(section.type, { [field]: value });
  };

  const updateResellerCardContent = (sectionId, cardIndex, field, value, valueType) => {
    const section = pageSections.find(item => item.id === sectionId);
    const currentCards = section?.contentOverrides?.cards || section?.cards;
    if (!section || !Array.isArray(currentCards) || !currentCards[cardIndex]) return;
    const cards = currentCards.map((card, index) => index === cardIndex
      ? { ...card, [field]: valueType === 'number' ? (value === '' ? '' : Number(value)) : value }
      : card);
    updateResellerSectionContent(sectionId, 'cards', cards);
  };

  const handleResellerCardImageUpload = (sectionId, cardIndex, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Choose an image file to upload.');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Image file size must be less than 3MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        toast.error('The selected image could not be read.');
        return;
      }
      updateResellerCardContent(sectionId, cardIndex, 'image', reader.result, 'string');
      updateResellerCardContent(sectionId, cardIndex, 'imageAlt', file.name.replace(/\.[^/.]+$/, ''), 'string');
    };
    reader.onerror = () => toast.error('The selected image could not be read.');
    reader.readAsDataURL(file);
  };

  const saveResellerContent = () => {
    try {
      const key = getBuilderStorageKey();
      const existing = JSON.parse(localStorage.getItem(key) || '{}');
      const pages = {
        ...Object.fromEntries(Object.entries(templatePages).map(([pageIndex, page]) => {
          const sections = Array.isArray(page?.sections) ? page.sections : Array.isArray(page) ? page : [];
          return [pageIndex, {
            ...(Array.isArray(page) ? {} : page),
            sections: applyResellerContentOverrides(
              applySharedStorefrontContent(sections, sharedContent),
              resellerContentOverrides
            ),
          }];
        })),
        [activeTemplatePage]: {
          ...(templatePages[activeTemplatePage] || {}),
          sections: applyResellerContentOverrides(
            applySharedStorefrontContent(pageSections, sharedContent),
            resellerContentOverrides
          ),
          published: false,
        },
      };
      localStorage.setItem(key, JSON.stringify({
        ...existing,
        pages,
        sections: pages[activeTemplatePage].sections,
        activeTemplatePage,
        contentOverridesBySectionType: resellerContentOverrides,
        published: false,
        status: 'Draft',
        updatedAt: new Date().toISOString(),
      }));
      setTemplatePages(pages);
      setPublished(false);
      setResellerContentSaved(true);
      window.dispatchEvent(new Event('cloudsetu-page-builder-updated'));
      toast.success('Section content saved in this browser.');
      return true;
    } catch (error) {
      toast.error(`Could not save section content locally: ${error.message}`);
      return false;
    }
  };

  const previewResellerContent = () => {
    if (!saveResellerContent()) return;
    const previewPath = `/storefront/preview?source=reseller&builderKey=${encodeURIComponent(getBuilderStorageKey())}&page=${activeTemplatePage}`;
    navigate(previewPath);
  };

  const pageVariants = adminCatalog.map(({ index: templateIndex, name }) => {
    const sections = createCompleteTemplatePage(templateIndex);
    return {
      templateIndex,
      design: { name },
      sections,
      previewSection: (() => {
        const section = sections.find(item => item.type === 'hero') || sections[0];
        if (!section) return null;
        return section;
      })(),
    };
  });
  const contentSection = pageSections.find(section => section.id === selectedContentSectionId) || pageSections[0];

  if (!loading && activeTab === 'builder' && showReadyMadeSites) {
    return (
      <DashboardLayout role="reseller">
        <div className="space-y-6" data-testid="reseller-ready-sites-library">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-blue-600">White-label storefront</p>
              <h1 className="mt-1 text-2xl font-bold text-slate-900">Ready-made sites</h1>
              <p className="mt-1 text-sm text-slate-500">Choose an Admin-approved design, edit your private content, preview, and save your storefront draft.</p>
            </div>
            <Button type="button" variant="outline" onClick={exitBuilder}>Back to settings</Button>
          </div>

          <section aria-labelledby="ready-sites-title" className="space-y-4">
            <div>
              <h2 id="ready-sites-title" className="text-lg font-semibold text-slate-900">Choose a design</h2>
              <p className="text-sm text-slate-500">Select a design to open its content editor. Page layout stays under Admin control.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {!pageVariants.length && (
                <p className="col-span-full rounded-lg border border-dashed border-slate-300 p-6 text-sm text-slate-500">
                  No ready-made sites are available yet. Admin must enable the Hero section and include it in an Admin page design.
                </p>
              )}
              {pageVariants.map(({ templateIndex, design, sections, previewSection }, index) => {
                const selected = templateIndex === activeTemplatePage;
                return (
                  <Card key={templateIndex} className={`overflow-hidden ${selected ? 'border-blue-500 ring-2 ring-blue-100' : ''}`} data-testid={`ready-site-card-${index + 1}`}>
                    <div
                      className="storefront-theme relative h-40 overflow-hidden bg-slate-950"
                      style={{
                        '--storefront-primary': formData.primary_color,
                        '--storefront-accent': formData.accent_color,
                        '--storefront-background': formData.background_color,
                      }}
                    >
                      <div className="pointer-events-none absolute inset-0 w-[400%] origin-top-left scale-[0.25]" aria-hidden="true">
                        {previewSection ? <StorefrontSection section={previewSection} /> : null}
                      </div>
                      <div className="absolute inset-x-0 top-0 flex items-center justify-between p-3 text-xs font-semibold text-white">
                        <span className="rounded-full bg-slate-950/45 px-2.5 py-1 backdrop-blur">SITE {String(index + 1).padStart(2, '0')}</span>
                        <span className="rounded-full bg-white/15 px-2.5 py-1 backdrop-blur">{selected ? 'Selected' : 'Ready to use'}</span>
                      </div>
                    </div>
                    <CardContent className="space-y-3 p-4">
                      <div className="min-h-16">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Admin-approved design</p>
                        <h3 className="mt-1 text-base font-semibold text-slate-900">{design.name}</h3>
                        <p className="mt-1 text-xs leading-5 text-slate-500">Layout and section variants set by Admin.</p>
                      </div>
                      <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
                        <span className="text-xs text-slate-500">{sections.length} sections · Editable content</span>
                      <Button type="button" size="sm" variant="default" onClick={() => {
                        const updatedPages = {
                          ...templatePages,
                          [activeTemplatePage]: { ...(templatePages[activeTemplatePage] || {}), sections: pageSections, published },
                        };
                        const rawSections = createCompleteTemplatePage(templateIndex);
                        const normalizedSections = applyAdminSectionAvailability(
                          applyResellerContentOverrides(applySharedStorefrontContent(rawSections, sharedContent), resellerContentOverrides),
                          (() => {
                            try { return JSON.parse(localStorage.getItem('cloudsetu_admin_section_library') || 'null'); }
                            catch { return null; }
                          })(),
                          settings?.partner_tier || settings?.tier || 'Premium'
                        );
                        const pages = {
                          ...updatedPages,
                          [templateIndex]: { name: design.name, sections: normalizedSections, published: false },
                        };
                        setTemplatePages(pages);
                        setActiveTemplatePage(templateIndex);
                        setPageSections(normalizedSections);
                        setSelectedSectionId(normalizedSections[0]?.id || null);
                        setSelectedContentSectionId(normalizedSections[0]?.id || '');
                        setPublished(false);
                        try {
                          const existing = JSON.parse(localStorage.getItem(getBuilderStorageKey()) || '{}');
                          localStorage.setItem(getBuilderStorageKey(), JSON.stringify({
                            ...existing,
                            pages,
                            sections: normalizedSections,
                            activeTemplatePage: templateIndex,
                            sharedContent,
                            contentOverridesBySectionType: resellerContentOverrides,
                          }));
                        } catch (error) {
                          toast.error(`Could not save your selected site locally: ${error.message}`);
                          return;
                        }
                        setShowReadyMadeSites(false);
                        setResellerContentSaved(true);
                        toast.success('Design selected. You can now update its section content.');
                      }}>
                        {selected ? 'Customize design' : 'Use this design'}
                      </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

        </div>
      </DashboardLayout>
    );
  }

  if (!loading && activeTab === 'builder' && !showReadyMadeSites) {
    return (
      <DashboardLayout role="reseller">
        <div className="space-y-6" data-testid="reseller-content-editor">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-blue-600">Approved storefront design</p>
              <h1 className="mt-1 text-2xl font-bold text-slate-900">Edit {pageVariants.find(item => item.templateIndex === activeTemplatePage)?.design.name || 'site'} content</h1>
              <p className="mt-1 text-sm text-slate-500">Admin manages the layout, variants, and responsive design. Edit only your private section content, then preview.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" onClick={() => setShowReadyMadeSites(true)}>Back to designs</Button>
              <Button type="button" variant="outline" onClick={previewResellerContent}>Preview</Button>
              <Button type="button" onClick={saveResellerContent} disabled={resellerContentSaved}>Save draft</Button>
              <Button
                type="button"
                data-testid="publish-storefront-btn"
                onClick={handlePublishClick}
                disabled={builderSaving}
              >
                {builderSaving ? 'Publishing…' : 'Publish to live site'}
              </Button>
            </div>
          </div>
          <p role="status" className={`text-xs ${published ? 'text-emerald-700' : resellerContentSaved ? 'text-blue-600' : 'text-amber-700'}`}>
            {published ? 'Site is live. Visible at your Storefront URL.' : resellerContentSaved ? 'Draft saved in this browser — not yet live.' : 'You have unsaved content changes.'}
          </p>

          <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)]">
            <Card className="h-fit min-w-0 overflow-hidden">
              <CardHeader className="border-b border-slate-100 py-4">
                <CardTitle className="text-sm">Admin-approved sections</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 p-3">
                {pageSections.map(section => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setSelectedContentSectionId(section.id)}
                    aria-current={contentSection?.id === section.id ? 'true' : undefined}
                    className={`w-full rounded-lg border p-3 text-left transition ${
                      contentSection?.id === section.id
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`reseller-content-section-${section.type}`}
                  >
                    <span className="block text-sm font-semibold text-slate-800">{section.name}</span>
                    <span className="mt-1 block text-xs text-slate-500">{section.variant}</span>
                    {section.visible === false && <span className="mt-1 block text-xs text-amber-700">Hidden by Admin</span>}
                  </button>
                ))}
                {!pageSections.length && (
                  <p className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                    This approved design has no editable sections. Ask Admin to add sections to its page template.
                  </p>
                )}
              </CardContent>
            </Card>

            {contentSection ? (
              <Card className="min-w-0" data-testid="reseller-section-content-form">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-base">{contentSection.name} content</CardTitle>
                  <p className="text-xs text-slate-500">Content only. The section variant and responsive design are managed by Admin.</p>
                </CardHeader>
                <CardContent className="space-y-5 p-5">
                  {!['footer', 'trust'].includes(contentSection.type) && (
                    <div className="space-y-1.5">
                      <Label htmlFor="reseller-section-heading">Heading</Label>
                      <Input
                        id="reseller-section-heading"
                        value={contentSection.contentOverrides?.heading ?? contentSection.heading ?? ''}
                        onChange={event => updateResellerSectionContent(contentSection.id, 'heading', event.target.value)}
                        data-testid="reseller-content-heading"
                      />
                    </div>
                  )}
                  {Object.prototype.hasOwnProperty.call(contentSection, 'kicker') && (
                    <div className="space-y-1.5">
                      <Label htmlFor="reseller-section-kicker">Eyebrow text</Label>
                      <Input
                        id="reseller-section-kicker"
                        value={contentSection.contentOverrides?.kicker ?? contentSection.kicker ?? ''}
                        onChange={event => updateResellerSectionContent(contentSection.id, 'kicker', event.target.value)}
                      />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label>Description and content</Label>
                    <RichTextEditor
                      value={contentSection.contentOverrides?.subtext ?? contentSection.subtext ?? ''}
                      onChange={value => updateResellerSectionContent(contentSection.id, 'subtext', value)}
                      placeholder="Edit this section's description"
                      minHeight="180px"
                      className="w-full min-w-0"
                    />
                  </div>
                  {['hero', 'cta'].includes(contentSection.type) && (
                    <div className="space-y-1.5">
                      <Label htmlFor="reseller-section-button">Button text</Label>
                      <Input
                        id="reseller-section-button"
                        value={contentSection.contentOverrides?.primaryText ?? contentSection.primaryText ?? ''}
                        onChange={event => updateResellerSectionContent(contentSection.id, 'primaryText', event.target.value)}
                      />
                    </div>
                  )}
                  {Object.prototype.hasOwnProperty.call(contentSection, 'secondaryText') && (
                    <div className="space-y-1.5">
                      <Label htmlFor="reseller-section-secondary-button">Secondary button text</Label>
                      <Input
                        id="reseller-section-secondary-button"
                        value={contentSection.contentOverrides?.secondaryText ?? contentSection.secondaryText ?? ''}
                        onChange={event => updateResellerSectionContent(contentSection.id, 'secondaryText', event.target.value)}
                      />
                    </div>
                  )}

                  {(contentSection.contentOverrides?.cards ?? contentSection.cards ?? []).map((card, cardIndex) => {
                    const editableFields = Object.entries(card).filter(([field, value]) => (
                      !['id', 'icon', 'image', 'imageUrl', 'src', 'url', 'href', 'color'].includes(field)
                      && (typeof value === 'string' || typeof value === 'number'
                        || (Array.isArray(value) && value.every(item => typeof item === 'string')))
                    ));
                    if (!editableFields.length) return null;
                    return (
                      <fieldset key={card.id || cardIndex} className="space-y-3 rounded-lg border border-slate-200 p-4">
                        <legend className="px-1 text-sm font-semibold text-slate-700">
                          {card.title || card.question || card.name || `Content item ${cardIndex + 1}`}
                        </legend>
                        {editableFields.map(([field, value]) => {
                          const label = field.replace(/([A-Z])/g, ' $1').replace(/^./, letter => letter.toUpperCase());
                          const isLongText = Array.isArray(value) || /text|description|answer|summary|quote|features/i.test(field);
                          return (
                            <div key={field} className="space-y-1">
                              <Label htmlFor={`reseller-card-${cardIndex}-${field}`} className="text-xs">{label}</Label>
                              {Array.isArray(value) || isLongText ? (
                                Array.isArray(value) ? (
                                  <textarea
                                    id={`reseller-card-${cardIndex}-${field}`}
                                    value={value.join('\n')}
                                    onChange={event => updateResellerCardContent(
                                      contentSection.id,
                                      cardIndex,
                                      field,
                                      event.target.value.split('\n'),
                                      typeof value
                                    )}
                                    rows={Math.min(Math.max(value.length + 1, 3), 7)}
                                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                                  />
                                ) : (
                                  <RichTextEditor
                                    value={String(value ?? '')}
                                    onChange={nextValue => updateResellerCardContent(contentSection.id, cardIndex, field, nextValue, typeof value)}
                                    placeholder={`Edit ${label.toLowerCase()}`}
                                    minHeight="120px"
                                    className="w-full min-w-0"
                                  />
                                )
                              ) : (
                                <Input
                                  id={`reseller-card-${cardIndex}-${field}`}
                                  type={typeof value === 'number' ? 'number' : 'text'}
                                  value={value}
                                  onChange={event => updateResellerCardContent(contentSection.id, cardIndex, field, event.target.value, typeof value)}
                                />
                              )}
                            </div>
                          );
                        })}
                        {('icon' in card) && (
                          <div className="space-y-2">
                            <Label htmlFor={`reseller-card-${cardIndex}-icon`} className="text-xs">Icon or emoji</Label>
                            <Input
                              id={`reseller-card-${cardIndex}-icon`}
                              value={card.icon || ''}
                              onChange={event => updateResellerCardContent(contentSection.id, cardIndex, 'icon', event.target.value, 'string')}
                              placeholder="Choose or paste an emoji"
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <label className="space-y-1 text-xs text-slate-600">
                                Icon width (px)
                                <Input type="number" min="16" max="500" value={card.iconWidth || ''} onChange={event => updateResellerCardContent(contentSection.id, cardIndex, 'iconWidth', event.target.value, 'number')} />
                              </label>
                              <label className="space-y-1 text-xs text-slate-600">
                                Icon height (px)
                                <Input type="number" min="16" max="500" value={card.iconHeight || ''} onChange={event => updateResellerCardContent(contentSection.id, cardIndex, 'iconHeight', event.target.value, 'number')} />
                              </label>
                            </div>
                          </div>
                        )}
                        <div className="space-y-2 rounded-md border border-slate-200 p-3">
                          <Label className="text-xs">Card image</Label>
                          <Input
                            value={card.image?.startsWith('data:image/') ? 'Uploaded image saved in this browser' : card.image || ''}
                            onChange={event => updateResellerCardContent(contentSection.id, cardIndex, 'image', event.target.value, 'string')}
                            placeholder="https://example.com/image.jpg"
                            aria-label={`Card ${cardIndex + 1} image URL`}
                          />
                          <input
                            type="file"
                            accept="image/*"
                            aria-label={`Upload image for card ${cardIndex + 1}`}
                            onChange={event => {
                              handleResellerCardImageUpload(contentSection.id, cardIndex, event.target.files?.[0]);
                              event.target.value = '';
                            }}
                            className="block w-full text-xs"
                          />
                          <Input
                            value={card.imageAlt || ''}
                            onChange={event => updateResellerCardContent(contentSection.id, cardIndex, 'imageAlt', event.target.value, 'string')}
                            placeholder="Alt text for accessibility"
                            aria-label={`Card ${cardIndex + 1} image alt text`}
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <label className="space-y-1 text-xs text-slate-600">
                              Image width (px)
                              <Input type="number" min="20" max="1600" value={card.imageWidth || ''} onChange={event => updateResellerCardContent(contentSection.id, cardIndex, 'imageWidth', event.target.value, 'number')} />
                            </label>
                            <label className="space-y-1 text-xs text-slate-600">
                              Image height (px)
                              <Input type="number" min="20" max="1200" value={card.imageHeight || ''} onChange={event => updateResellerCardContent(contentSection.id, cardIndex, 'imageHeight', event.target.value, 'number')} />
                            </label>
                          </div>
                          {card.image && <img src={card.image} alt={card.imageAlt || card.title || ''} className="max-h-32 max-w-full rounded border border-slate-200 object-contain" />}
                        </div>
                      </fieldset>
                    );
                  })}
                </CardContent>
              </Card>
            ) : null}
          </div>
          <p className="text-xs text-slate-500">Layout, section order, visibility, styles, and section creation are controlled by Admin. Content changes are stored in this browser; live publishing requires the configured backend.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="reseller">
      <div className="space-y-6">
        {/* Page Header */}
        {activeTab !== 'builder' && <div className="mb-2">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="white-label-page-title">
            White Label Settings
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">Customize your brand, domain, SSL, and storefront page.</p>
        </div>}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Tabs */}
            {activeTab !== 'builder' && <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-full lg:w-fit overflow-x-auto">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => {
                      if (tab.key === 'builder') {
                        const url = new URL(window.location.href);
                        url.searchParams.set('tab', 'builder');
                        window.history.replaceState({}, '', url.toString());
                        setShowReadyMadeSites(true);
                        setActiveTab('builder');
                        return;
                      }
                      const url = new URL(window.location.href);
                      url.searchParams.delete('tab');
                      window.history.replaceState({}, '', url.toString());
                      setActiveTab(tab.key);
                    }}
                    data-testid={`wl-tab-${tab.key}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${activeTab === tab.key
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                      }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>}

            {/* ── BRANDING TAB ────────────────────────────────────────────── */}
            {activeTab === 'branding' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card data-testid="settings-form-card">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Palette className="h-5 w-5" />
                      <span>Customization</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSaveBranding} className="space-y-6">
                      {/* Storefront URL — always visible */}
                      <div className="bg-slate-50 border border-slate-200 rounded-md p-4 space-y-1">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Your Storefront URL</p>
                        {settings?.subdomain ? (
                          <a
                            href={`/public/${settings.subdomain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-sm font-mono block"
                            data-testid="subdomain-link"
                          >
                            {settings.subdomain}.cloudsetu.com
                          </a>
                        ) : (
                          <p className="text-sm text-slate-400 italic">
                            Not configured — go to <button type="button" className="text-blue-600 underline" onClick={() => setActiveTab('domain')}>Domain Setup</button>
                          </p>
                        )}
                      </div>

                      {/* Company Name */}
                      <div className="space-y-2">
                        <Label htmlFor="company_name">Company Name</Label>
                        <Input
                          id="company_name"
                          value={formData.company_name}
                          onChange={e => setFormData(p => ({ ...p, company_name: e.target.value }))}
                          placeholder="Your company name"
                          data-testid="wl-company-name-input"
                        />
                        <p className="text-xs text-slate-400">Shown in your storefront header and footer</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="primary_color">Primary Color</Label>
                        <div className="flex space-x-2">
                          <Input id="primary_color" type="color" value={formData.primary_color}
                            onChange={e => setFormData(p => ({ ...p, primary_color: e.target.value }))}
                            className="w-20 h-10" data-testid="primary-color-input" />
                          <Input type="text" value={formData.primary_color}
                            onChange={e => setFormData(p => ({ ...p, primary_color: e.target.value }))}
                            className="flex-1" data-testid="primary-color-text-input" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="accent_color">Accent Color</Label>
                        <div className="flex space-x-2">
                          <Input id="accent_color" type="color" value={formData.accent_color}
                            onChange={e => setFormData(p => ({ ...p, accent_color: e.target.value }))}
                            className="w-20 h-10" data-testid="accent-color-input" />
                          <Input type="text" value={formData.accent_color}
                            onChange={e => setFormData(p => ({ ...p, accent_color: e.target.value }))}
                            className="flex-1" data-testid="accent-color-text-input" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="background_color">Section Background Color</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="background_color"
                            type="color"
                            value={formData.background_color}
                            onChange={event => setFormData(current => ({ ...current, background_color: event.target.value }))}
                            className="h-10 w-20"
                            data-testid="background-color-input"
                          />
                          <Input
                            type="text"
                            value={formData.background_color}
                            onChange={event => setFormData(current => ({ ...current, background_color: event.target.value }))}
                            className="flex-1"
                            data-testid="background-color-text-input"
                          />
                        </div>
                        <p className="text-xs text-slate-400">Applied to light sections across every approved template.</p>
                      </div>

                      <div className="space-y-2">
                        <Label>Color Presets</Label>
                        <div className="grid grid-cols-5 gap-2">
                          {presetColors.map((preset, index) => (
                            <button key={index} type="button"
                              onClick={() => setFormData(p => ({ ...p, primary_color: preset.primary, accent_color: preset.accent, background_color: preset.background }))}
                              className="flex flex-col items-center p-2 border rounded-md hover:border-blue-500 transition-colors"
                              title={preset.name} data-testid={`color-preset-${index}`}>
                              <div className="flex space-x-1">
                                <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.primary }}></div>
                                <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.accent }}></div>
                              </div>
                              <span className="text-xs mt-1 text-slate-600">{preset.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Your Logo</Label>
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-4">
                          {formData.logo_url ? (
                            <div className="flex items-center gap-4">
                              <div className="w-20 h-20 bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center">
                                <img src={getLogoDisplayUrl(formData.logo_url)} alt="Your logo"
                                  className="w-full h-full object-contain p-2"
                                  onError={e => { e.target.style.display = 'none'; }} />
                              </div>
                              <div className="flex-1 space-y-2">
                                <p className="text-sm text-slate-600">Your custom logo is set</p>
                                <div className="flex gap-2">
                                  <Button type="button" variant="outline" size="sm"
                                    onClick={() => fileInputRef.current?.click()} disabled={uploading}
                                    data-testid="change-logo-btn">
                                    <Upload className="h-4 w-4 mr-2" /> Change
                                  </Button>
                                  <Button type="button" variant="destructive" size="sm"
                                    onClick={handleDeleteLogo} data-testid="delete-logo-btn">
                                    <Trash2 className="h-4 w-4 mr-2" /> Remove
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center py-6 cursor-pointer hover:bg-slate-50 rounded-lg transition-colors"
                              onClick={() => fileInputRef.current?.click()}>
                              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                                <ImageIcon className="h-8 w-8 text-slate-400" />
                              </div>
                              <p className="text-sm font-medium text-slate-700">Upload your logo</p>
                              <p className="text-xs text-slate-500 mt-1">PNG, JPG, GIF up to 2MB</p>
                              <Button type="button" variant="outline" size="sm" className="mt-3"
                                disabled={uploading} data-testid="upload-logo-btn">
                                <Upload className="h-4 w-4 mr-2" />
                                {uploading ? 'Uploading...' : 'Select File'}
                              </Button>
                            </div>
                          )}
                          <input ref={fileInputRef} type="file"
                            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                            onChange={handleLogoUpload} className="hidden" data-testid="logo-file-input" />
                        </div>
                        <p className="text-xs text-slate-500">
                          Your logo will appear in the sidebar and header of your dashboard
                        </p>
                      </div>

                      <div className="flex items-center space-x-2">
                        <input type="checkbox" id="show_pricing" checked={formData.show_pricing}
                          onChange={e => setFormData(p => ({ ...p, show_pricing: e.target.checked }))}
                          className="w-4 h-4" data-testid="show-pricing-checkbox" />
                        <Label htmlFor="show_pricing">Show pricing on public page</Label>
                      </div>

                      <Button type="submit" className="w-full" data-testid="save-settings-button">
                        Save Settings
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Preview Card */}
                <Card data-testid="preview-card">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Eye className="h-5 w-5" />
                      <span>Live Preview</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="p-6 flex items-center gap-4" style={{ backgroundColor: formData.primary_color }}>
                        {formData.logo_url && (
                          <div className="w-12 h-12 bg-white rounded-lg overflow-hidden flex items-center justify-center">
                            <img src={getLogoDisplayUrl(formData.logo_url)} alt="Logo preview"
                              className="w-full h-full object-contain p-1"
                              onError={e => { e.target.style.display = 'none'; }} />
                          </div>
                        )}
                        <h2 className="text-2xl font-bold text-white">{formData.company_name || settings?.company_name}</h2>
                      </div>
                      <div className="p-6 bg-white space-y-4">
                        <div className="border rounded-lg p-4">
                          <h3 className="font-semibold mb-2">Sample Product</h3>
                          <p className="text-sm text-slate-600 mb-3">Shared Hosting - Basic Plan</p>
                          <button className="px-4 py-2 rounded-md text-white font-medium"
                            style={{ backgroundColor: formData.accent_color }}>
                            Order Now
                          </button>
                        </div>
                        <p className="text-xs text-slate-500">This is how your white-label page will look</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* ── PAGE BUILDER TAB ────────────────────────────────────────── */}
            {activeTab === 'builder' && (
              <div
                ref={builderRootRef}
                className="builder-theme fixed inset-0 z-[100] overflow-auto bg-slate-50 p-5 space-y-4"
                data-builder-root
                style={{
                  '--storefront-primary': formData.primary_color,
                  '--storefront-accent': formData.accent_color,
                  '--storefront-background': formData.background_color,
                }}
              >
                <div className="flex items-start justify-between gap-4 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-white px-5 py-4">
                  <div>
                    <p className="text-xs font-extrabold uppercase tracking-wider text-blue-600">White Label Page Builder</p>
                    <h1 className="mt-1 text-xl font-bold text-slate-900">Welcome to your own White-Label Settings</h1>
                    <p className="mt-1 text-sm text-slate-500">Customize your storefront with your branding, sections, and content.</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowReadyMadeSites(true)}>
                    Back to designs
                  </Button>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button type="button" variant="outline" size="sm" data-builder-action="preview" data-testid="builder-preview-storefront">
                    <ExternalLink className="h-4 w-4 mr-2" /> Preview Storefront
                  </Button>
                  <Button type="button" variant="outline" size="sm" disabled={builderSaving} data-builder-action="save-draft" data-testid="builder-save-draft">
                    <Save className="h-4 w-4 mr-2" /> Save Draft
                  </Button>
                  <Button type="button" size="sm" disabled={builderSaving} data-builder-action="publish" data-testid="builder-publish">
                    <CheckCircle className="h-4 w-4 mr-2" /> Publish
                  </Button>
                </div>
                <div className={`grid grid-cols-1 gap-4 ${showRightEditorInBuilder
                  ? 'xl:grid-cols-[220px_minmax(420px,1fr)_260px] 2xl:grid-cols-[260px_minmax(480px,1fr)_340px]'
                  : 'xl:grid-cols-[220px_minmax(420px,1fr)] 2xl:grid-cols-[260px_minmax(480px,1fr)]'}`}>
                  <style>{`
                    [data-builder-root] .builder-section-content .bg-blue-600,
                    [data-builder-root] .builder-section-content .bg-blue-700,
                    [data-builder-root] .builder-section-content .bg-blue-500 {
                      background-color: var(--storefront-accent) !important;
                    }
                    [data-builder-root] .builder-section-content .text-blue-600,
                    [data-builder-root] .builder-section-content .text-blue-700,
                    [data-builder-root] .builder-section-content .text-blue-500 {
                      color: var(--storefront-accent) !important;
                    }
                    [data-builder-root] .builder-section-content .bg-slate-950,
                    [data-builder-root] .builder-section-content .bg-slate-900 {
                      background-color: var(--storefront-primary) !important;
                    }
                    [data-builder-root] .builder-section-content .bg-slate-50,
                    [data-builder-root] .builder-section-content .bg-blue-50 {
                      background-color: var(--storefront-background) !important;
                    }
                    [data-builder-section-id] h1,
                    [data-builder-section-id] h2,
                    [data-builder-section-id] h3,
                    [data-builder-section-id] h4,
                    [data-builder-section-id] h5,
                    [data-builder-section-id] h6 {
                      font-family: var(--builder-section-heading-font) !important;
                    }
                    [data-builder-section-id] section {
                      font-family: var(--builder-section-paragraph-font);
                      padding: var(--builder-section-padding-y) var(--builder-section-padding-x) !important;
                    }
                    [data-builder-section-id] p {
                      font-family: var(--builder-section-paragraph-font) !important;
                    }
                  `}</style>
                  <Card className="overflow-hidden">
                    <CardHeader className="py-3 px-4 border-b"><CardTitle className="text-sm">Section Library</CardTitle></CardHeader>
                    <CardContent className="h-[680px] overflow-y-auto p-3 space-y-2">
                      {getAccessibleSectionLibrary().map(librarySection => (
                        <div key={librarySection.type} className="overflow-hidden rounded-lg border border-slate-200">
                          <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2.5">
                            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-100 text-blue-600">
                              <LayoutTemplate className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-semibold text-slate-800">{librarySection.name}</p>
                              <p className="text-[10px] text-slate-400">{librarySection.variants.length} templates</p>
                            </div>
                          </div>
                          <div className="space-y-1 p-2">
                            {librarySection.variants.map((variantName, variantIndex) => (
                              <button type="button" key={variantName}
                                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[11px] text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                                data-builder-action="add" data-library-type={librarySection.type} data-variant={variantName}
                                data-testid={`builder-add-${librarySection.type}-${variantIndex + 1}`}>
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-slate-100 text-[9px] font-bold text-slate-500">{variantIndex + 1}</span>
                                <span className="flex-1 truncate">{variantName}</span>
                                <Plus className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  <Card className="overflow-hidden">
                    <CardHeader className="py-3 px-4 border-b flex-row items-center justify-between">
                      <CardTitle className="mr-auto text-sm">Canvas</CardTitle>
                      <p className="mr-2 hidden text-[10px] text-slate-400 md:block">Click to select · double-click text to edit</p>
                      <div className="flex items-center gap-1.5">
                        <div className="relative">
                          <button
                            type="button"
                            className="h-8 rounded-md border border-slate-200 bg-white px-2 text-[10px] font-semibold text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                            aria-label="Open emoji picker"
                            onClick={() => setEmojiPickerOpen(open => !open)}
                          >😀 Emoji</button>
                          {emojiPickerOpen && (
                            <div className="absolute right-0 top-9 z-50 w-[340px] rounded-xl border border-slate-200 bg-white p-3 shadow-xl" onClick={event => event.stopPropagation()}>
                              <div className="mb-2 flex items-center justify-between gap-2">
                                <span className="text-xs font-bold text-slate-800">Emoji picker</span>
                                <button type="button" className="text-xs text-slate-500 hover:text-slate-900" onClick={() => setEmojiPickerOpen(false)}>Close</button>
                              </div>
                              <div className="mb-2 flex gap-1">
                                {[['canvas', 'Add to canvas'], ['text', 'Insert in text']].map(([target, label]) => (
                                  <button
                                    key={target}
                                    type="button"
                                    className={`rounded px-2 py-1 text-[10px] font-semibold ${emojiInsertTarget === target ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                                    data-emoji-insert-target={target}
                                  >{label}</button>
                                ))}
                              </div>
                              <input
                                type="search"
                                value={emojiSearch}
                                onChange={event => setEmojiSearch(event.target.value)}
                                placeholder="Search or paste an emoji"
                                className="mb-2 h-8 w-full rounded-md border border-slate-200 px-2 text-xs outline-none focus:border-blue-400"
                                aria-label="Search emojis"
                              />
                              {!emojiSearch && (
                                <div className="mb-2 flex gap-1 overflow-x-auto pb-1">
                                  {Object.keys(BUILDER_EMOJI_GROUPS).map(category => (
                                    <button
                                      key={category}
                                      type="button"
                                      className={`shrink-0 rounded px-2 py-1 text-[9px] ${emojiPickerCategory === category ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}
                                      onClick={() => setEmojiPickerCategory(category)}
                                    >{category}</button>
                                  ))}
                                </div>
                              )}
                              <div className="grid max-h-52 grid-cols-8 gap-1 overflow-y-auto">
                                {(emojiSearch
                                  ? Object.values(BUILDER_EMOJI_GROUPS).flat().filter(emoji => emoji.includes(emojiSearch))
                                  : BUILDER_EMOJI_GROUPS[emojiPickerCategory] || []
                                ).slice(0, 800).map((emoji, index) => (
                                  <button
                                    key={`${emoji}-${index}`}
                                    type="button"
                                    className="flex h-8 w-8 items-center justify-center rounded text-xl hover:bg-blue-50"
                                    data-builder-emoji={emoji}
                                    title={emoji}
                                  >{emoji}</button>
                                ))}
                              </div>
                              <p className="mt-2 text-[9px] text-slate-400">Emoji appearance depends on the device font.</p>
                            </div>
                          )}
                        </div>
                        <select
                          aria-label="Add shape"
                          title="Add a shape to the selected section"
                          className="h-8 w-28 rounded-md border border-slate-200 bg-white px-2 text-[10px] font-semibold text-slate-600"
                          data-builder-shape-select="true"
                          value=""
                        >
                          <option value="">＋ Add shape</option>
                          <option value="rectangle">▭ Rectangle</option>
                          <option value="circle">◯ Circle</option>
                          <option value="line">／ Line</option>
                        </select>
                        <select
                          aria-label="Font applies to"
                          className="h-8 rounded-md border border-slate-200 bg-white px-2 text-[11px] text-slate-700"
                          data-builder-font-target="true"
                          value={fontApplyTarget}
                        >
                          <option value="both">Both</option>
                          <option value="heading">Headings</option>
                          <option value="paragraph">Paragraphs</option>
                        </select>
                        <select
                          aria-label="Global font"
                          className="h-8 rounded-md border border-slate-200 bg-white px-2 text-[11px] text-slate-700"
                          data-builder-font="true"
                          value={fontSelection}
                        >
                          {BUILDER_FONTS.map(font => (
                            <option key={font.label} value={font.value}>{font.label}</option>
                          ))}
                        </select>
                        <select
                          aria-label="Complete page template"
                          className="h-8 rounded-md border border-slate-200 bg-white px-2 text-[11px] font-semibold text-slate-700"
                          data-builder-preset="true"
                          value={activeTemplatePage}
                        >
                          {pageVariants.map(({ design, templateIndex }) => (
                            <option key={templateIndex} value={templateIndex}>Page {templateIndex + 1} — {design.name}</option>
                          ))}
                        </select>
                        <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold text-blue-700">
                          Complete Page {activeTemplatePage + 1} · {pageSections.length} sections
                        </span>
                        <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${published ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {published ? 'Published' : 'Draft'}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="h-[680px] overflow-y-auto bg-slate-100 p-4" data-testid="builder-canvas">
                      <div className="min-h-[648px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                        {pageSections.map(section => (
                          <div key={section.id}
                            className={`relative cursor-pointer border-l-2 transition-colors ${selectedSectionId === section.id ? 'border-blue-500 outline outline-2 outline-dotted outline-blue-400 outline-offset-[-2px]' : 'border-transparent'} ${!section.visible ? 'opacity-45' : ''} ${draggedSectionId === section.id ? 'opacity-75' : ''}`}
                            style={{
                              width: `${section.width || 100}%`,
                              minHeight: section.height ? `${section.height}px` : undefined,
                              margin: `${section.marginY || 0}px ${section.marginX || 0}px`,
                              '--builder-section-heading-font': section.headingFontFamily || headingFontFamily,
                              '--builder-section-paragraph-font': section.paragraphFontFamily || paragraphFontFamily,
                              '--builder-section-padding-x': `${section.paddingX ?? 24}px`,
                              '--builder-section-padding-y': `${section.paddingY ?? (section.type === 'hero' ? 40 : 32)}px`,
                            }}
                            data-builder-action="select"
                            data-builder-section-id={section.id}
                            data-builder-section-type={section.type}
                            data-testid={`builder-canvas-section-${section.id}`}>
                            <div className="absolute right-2 top-2 z-10 flex gap-0.5 rounded-md border border-slate-200 bg-white p-1 shadow-sm">
                              <button type="button" title="Drag to move section" aria-label="Drag to move section" className="cursor-grab p-1 text-slate-400" style={{ touchAction: 'none' }} data-canvas-move data-section-id={section.id}>
                                <GripVertical className="h-5 w-3" />
                              </button>
                              <button type="button" title="Move up" className="p-1 text-slate-500 hover:text-blue-600" data-builder-action="move-up" data-section-id={section.id}>
                                <ChevronUp className="h-3.5 w-3.5" />
                              </button>
                              <button type="button" title="Move down" className="p-1 text-slate-500 hover:text-blue-600" data-builder-action="move-down" data-section-id={section.id}>
                                <ChevronDown className="h-3.5 w-3.5" />
                              </button>
                              <button type="button" title="Edit" className="p-1 text-slate-500 hover:text-blue-600 edit-section-btn" data-builder-action="select" data-section-id={section.id}>
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button type="button" title={section.visible ? 'Hide' : 'Show'} className="p-1 text-slate-500 hover:text-blue-600 visibility-section-btn" data-builder-action="visibility" data-section-id={section.id}>
                                {section.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                              </button>
                              <button type="button" title="Delete" className="p-1 text-slate-500 hover:text-red-600 delete-section-btn" data-builder-action="delete" data-section-id={section.id}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            {selectedSectionId === section.id && (
                              <div className="absolute bottom-2 right-2 z-20 flex flex-wrap items-center gap-1 rounded-md border border-blue-200 bg-white/95 p-1 shadow-sm">
                                {[
                                  ['width', 'W', 'ew-resize'],
                                  ['height', 'H', 'ns-resize'],
                                  ['paddingX', 'PX', 'ew-resize'],
                                  ['paddingY', 'PY', 'ns-resize'],
                                  ['marginX', 'MX', 'ew-resize'],
                                  ['marginY', 'MY', 'ns-resize'],
                                ].map(([mode, label, cursor]) => (
                                  <button
                                    key={mode}
                                    type="button"
                                    title={`Drag to adjust ${mode}`}
                                    aria-label={`Drag to adjust ${mode}`}
                                    className="h-6 min-w-7 rounded border border-slate-200 bg-slate-50 px-1 text-[9px] font-bold text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                                    style={{ cursor, touchAction: 'none' }}
                                    data-canvas-adjust={mode}
                                    data-section-id={section.id}
                                  >
                                    {label}
                                  </button>
                                ))}
                              </div>
                            )}
                            <div className="builder-section-content">
                            {section.type === 'header' && (
                              <StorefrontSection section={{
                                ...section,
                                brandName: formData.company_name || settings?.company_name || '',
                                brandLogo: formData.logo_url ? getLogoDisplayUrl(formData.logo_url) : '',
                              }} />
                            )}
                            {section.type === 'hero' && (
                              <section className={`px-6 py-10 text-white ${
                                section.variant === 'Dark Gradient Glow'
                                  ? ''
                                  : section.variant === 'Centered Minimal'
                                    ? 'text-center'
                                    : section.variant === 'Stats Showcase'
                                      ? 'text-center'
                                      : 'text-left'
                              }`} style={{ backgroundColor: formData.primary_color }}>
                                <div className={`grid items-center gap-5 ${section.variant?.toLowerCase().includes('centered') || section.variant === 'Stats Showcase' ? 'text-center' : ''} md:grid-cols-2`}>
                                  <div>
                                    <h1 data-shared-field="heading" className={`text-3xl font-bold ${section.variant?.toLowerCase().includes('modern split') ? 'max-w-xl' : 'mx-auto max-w-2xl'}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.heading || '') }} />
                                    <p data-shared-field="subtext" className={`mt-3 text-sm text-white/80 ${section.variant?.toLowerCase().includes('modern split') ? 'max-w-xl' : 'mx-auto max-w-2xl'}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.subtext || '') }} />
                                    <button type="button" data-shared-field="primaryText" className="mt-5 rounded-md px-5 py-2 text-sm font-semibold text-white" style={{ backgroundColor: formData.accent_color }}>{section.primaryText || 'Get Started'}</button>
                                  </div>
                                  {section.mediaUrl && (
                                    <img
                                      data-shared-field="mediaUrl"
                                      src={normalizeSafeUrl(section.mediaUrl)}
                                      alt={section.mediaAlt || ''}
                                      className="mx-auto max-h-56 w-full max-w-sm object-contain"
                                    />
                                  )}
                                </div>
                              </section>
                            )}
                            {section.type === 'features' && (
                              <section className={`px-6 py-8 ${section.variant === 'Icon Row List' ? 'text-left' : 'text-center'}`} style={{ backgroundColor: formData.background_color }}>
                                <h3 data-shared-field="heading" className="text-lg font-bold text-slate-800" dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.heading || '') }} /><p data-shared-field="subtext" className="text-xs text-slate-500 mt-1" dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.subtext || '') }} />
                                <div className={`mt-5 grid gap-3 ${
                                  section.variant === '2-Column Split Image' ? 'grid-cols-2'
                                    : section.variant === 'Icon Row List' ? 'grid-cols-1'
                                      : section.variant === 'Bento Grid' ? 'md:grid-cols-3'
                                        : 'grid-cols-3'
                                }`}>
                                  {(section.cards || []).map((card, index) => (
                                    <div key={`${card.title}-${index}`} className={`rounded-lg border border-slate-200 bg-white p-4 text-left ${section.variant === 'Bento Grid' && index === 0 ? 'md:col-span-2' : ''}`}>
                                      <div data-shared-card-index={index} data-shared-card-field="icon" className="text-lg" dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.icon || '') }} />
                                      <p data-shared-card-index={index} data-shared-card-field="title" className="mt-1 text-xs font-bold text-slate-800" dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.title || '') }} />
                                      <p data-shared-card-index={index} data-shared-card-field="text" className="mt-1 text-xs text-slate-500" dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.text || '') }} />
                                      {Array.isArray(card.list) && <ul className="mt-2 space-y-1 text-[10px] text-slate-500">{card.list.map((item, itemIndex) => <li key={`${item}-${itemIndex}`} data-shared-card-index={index} data-shared-card-field="list" data-shared-list-index={itemIndex}>✓ {item}</li>)}</ul>}
                                    </div>
                                  ))}
                                </div>
                              </section>
                            )}
                            {['pricing', 'testimonials', 'trust', 'cta', 'footer', 'faq', 'blog', 'slider'].includes(section.type) && (
                              <section className={`px-6 py-8 ${
                                section.type === 'cta' ? 'bg-blue-50 text-center'
                                  : section.type === 'pricing' ? 'bg-slate-50 text-center'
                                    : section.type === 'footer' ? 'bg-slate-900 text-white'
                                      : 'text-center'
                              }`} style={{ backgroundColor: section.type === 'footer' || section.type === 'testimonials' || section.type === 'slider' ? formData.primary_color : formData.background_color }}>
                                <h3 data-shared-field="heading" className={`text-lg font-bold ${section.type === 'footer' ? 'text-white' : 'text-slate-800'}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.heading || '') }} />
                                {section.subtext && <p data-shared-field="subtext" className={`mt-1 text-xs ${section.type === 'footer' ? 'text-slate-300' : 'text-slate-500'}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.subtext || '') }} />}
                                {section.type === 'pricing' && (
                                  <div className={`mt-5 grid gap-3 ${
                                    section.variant === 'Compact Plan List' ? 'grid-cols-1'
                                      : section.variant === 'Comparison Matrix Table' ? 'grid-cols-1'
                                        : 'grid-cols-3'
                                  }`}>
                                    {(section.cards || []).map((card, index) => (
                                      <div key={`${card.title}-${index}`} className={`rounded-lg border border-slate-200 bg-white p-4 text-left ${card.featured ? 'ring-2 ring-blue-500' : ''}`}>
                                        <p data-shared-card-index={index} data-shared-card-field="title" className="text-xs font-bold">{card.title}</p>
                                        <p className="mt-2 text-lg font-bold text-blue-600"><span data-shared-card-index={index} data-shared-card-field="price">{card.price}</span><span data-shared-card-index={index} data-shared-card-field="period" className="text-xs font-normal text-slate-500">{card.period}</span></p>
                                        <p data-shared-card-index={index} data-shared-card-field="text" className="mt-2 text-xs text-slate-500">{card.text}</p>
                                        {Array.isArray(card.list) && <ul className="mt-3 space-y-1 text-[10px] text-slate-500">{card.list.map((item, itemIndex) => <li key={`${item}-${itemIndex}`} data-shared-card-index={index} data-shared-card-field="list" data-shared-list-index={itemIndex}>✓ {item}</li>)}</ul>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {section.type === 'cta' && <button type="button" data-shared-field="primaryText" className="mt-4 rounded-md px-5 py-2 text-xs font-semibold text-white" style={{ backgroundColor: formData.accent_color }}>{section.primaryText || 'Start Now'}</button>}
                                {['testimonials', 'trust', 'blog', 'slider'].includes(section.type) && (
                                  <div className={`mt-5 grid gap-3 ${section.type === 'trust' ? 'grid-cols-3' : 'grid-cols-3'}`}>
                                    {(section.cards || []).map((card, index) => (
                                      <div key={`${card.title}-${index}`} className={`rounded-lg border border-slate-200 p-4 text-left ${section.type === 'trust' ? 'bg-blue-50' : 'bg-white'}`}>
                                        <div className="text-lg">{card.icon}</div>
                                        <p data-shared-card-index={index} data-shared-card-field={card.title ? 'title' : 'category'} className="text-xs font-bold text-slate-800">{card.title || card.category}</p>
                                        <p data-shared-card-index={index} data-shared-card-field={card.summary ? 'summary' : 'text'} className="mt-1 text-xs text-slate-600">{card.text || card.summary || card.date}</p>
                                        {card.author && <p className="mt-2 text-[10px] font-semibold text-slate-500"><span data-shared-card-index={index} data-shared-card-field="author">{card.author}</span> · <span data-shared-card-index={index} data-shared-card-field="role">{card.role}</span></p>}
                                        {section.type === 'slider' && <button type="button" data-shared-card-index={index} data-shared-card-field="buttonText" className="mt-3 rounded bg-blue-600 px-3 py-1 text-[10px] font-semibold text-white">{card.buttonText}</button>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {section.type === 'faq' && (
                                  <div className="mx-auto mt-5 max-w-2xl space-y-2 text-left">
                                    {(section.cards || []).map((card, index) => <div key={`${card.question}-${index}`} className="rounded-lg border border-slate-200 bg-white p-3"><p data-shared-card-index={index} data-shared-card-field="question" className="text-xs font-bold text-slate-800">{card.question}</p><p data-shared-card-index={index} data-shared-card-field="answer" className="mt-1 text-xs text-slate-500">{card.answer}</p></div>)}
                                  </div>
                                )}
                                {section.type === 'footer' && (
                                  <div className="mt-5 grid grid-cols-3 gap-3 text-left">
                                    {(section.cards || []).map((card, index) => <div key={`${card.title}-${index}`}><p data-shared-card-index={index} data-shared-card-field="title" className="text-xs font-bold">{card.title}</p><p data-shared-card-index={index} data-shared-card-field="text" className="mt-1 whitespace-pre-line text-[10px] text-slate-300">{card.text}</p></div>)}
                                  </div>
                                )}
                              </section>
                            )}
                            </div>
                            {(section.shapes || []).map(shape => {
                              const isShapeSelected = selectedShapeId === shape.id;
                              return (
                                <div
                                  key={shape.id}
                                  className="absolute z-20"
                                  style={{
                                    left: `${shape.x || 0}px`,
                                    top: `${shape.y || 0}px`,
                                    width: `${shape.width || 120}px`,
                                    height: `${shape.type === 'line' ? Math.max(2, shape.height || 4) : shape.height || 80}px`,
                                    display: shape.type === 'emoji' ? 'flex' : undefined,
                                    alignItems: shape.type === 'emoji' ? 'center' : undefined,
                                    justifyContent: shape.type === 'emoji' ? 'center' : undefined,
                                    fontSize: shape.type === 'emoji' ? `${Math.max(20, Math.min(shape.width || 64, shape.height || 64) * 0.75)}px` : undefined,
                                    lineHeight: shape.type === 'emoji' ? 1 : undefined,
                                    borderRadius: shape.type === 'emoji' ? undefined : shape.type === 'circle' ? '50%' : shape.type === 'line' ? '999px' : '4px',
                                    backgroundColor: shape.type === 'emoji' ? 'transparent' : shape.color || '#3b82f6',
                                    outline: isShapeSelected ? '2px dotted #1d4ed8' : undefined,
                                    outlineOffset: isShapeSelected ? '3px' : undefined,
                                  }}
                                  title="Drag to move shape"
                                  data-shape-move
                                  data-shape-id={shape.id}
                                  data-section-id={section.id}
                                >
                                  {shape.type === 'emoji' && <span aria-label="Canvas emoji">{shape.emoji}</span>}
                                  {isShapeSelected && (
                                    <>
                                      {shape.type !== 'emoji' && (
                                        <input
                                          type="color"
                                          aria-label="Shape color"
                                          className="absolute -left-1 -top-8 h-6 w-8 cursor-pointer rounded border border-slate-300 bg-white p-0.5"
                                          value={shape.color || '#3b82f6'}
                                          data-builder-shape-color={shape.id}
                                          data-section-id={section.id}
                                          onPointerDown={event => event.stopPropagation()}
                                        />
                                      )}
                                      <button
                                        type="button"
                                        className={`absolute ${shape.type === 'emoji' ? 'left-0' : 'left-9'} -top-8 rounded border border-slate-300 bg-white px-1.5 text-xs leading-5 text-red-600`}
                                        title="Delete shape"
                                        data-shape-action="delete"
                                        data-shape-id={shape.id}
                                        data-section-id={section.id}
                                      >×</button>
                                      <span
                                        className="absolute -bottom-1.5 -right-1.5 h-3.5 w-3.5 cursor-nwse-resize rounded border border-blue-700 bg-white"
                                        title="Drag to resize"
                                        data-shape-resize
                                        data-shape-id={shape.id}
                                        data-section-id={section.id}
                                      />
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                        {pageSections.length === 0 && (
                          <div className="flex h-[420px] items-center justify-center text-center">
                            <div><LayoutTemplate className="mx-auto h-9 w-9 text-slate-300" /><p className="mt-2 text-sm font-medium text-slate-500">Add a section from the library</p></div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {showRightEditorInBuilder && <Card className="overflow-hidden" data-testid="builder-properties-panel">
                    <CardHeader className="py-3 px-4 border-b"><CardTitle className="text-sm">{selectedSection ? `${selectedSection.name} Properties` : 'Properties'}</CardTitle></CardHeader>
                    <CardContent className="h-[680px] overflow-y-auto p-4 space-y-4">
                      {selectedSection ? (
                        <>
                          <div className="space-y-3 rounded-lg border border-slate-200 p-3">
                            <p className="text-xs font-semibold text-slate-700">Section typography</p>
                            <div className="space-y-1">
                              <Label className="text-xs">Heading font</Label>
                              <select
                                aria-label="Section heading font"
                                className="h-9 w-full rounded-md border border-slate-200 px-2 text-xs"
                                value={selectedSection.headingFontFamily || 'global'}
                                onChange={(event) => updateSelectedSection('headingFontFamily', event.target.value)}
                              >
                                <option value="global">Use global ({BUILDER_FONTS.find(font => font.value === headingFontFamily)?.label || 'Inter'})</option>
                                {BUILDER_FONTS.map(font => <option key={font.label} value={font.value}>{font.label}</option>)}
                              </select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Paragraph font</Label>
                              <select
                                aria-label="Section paragraph font"
                                className="h-9 w-full rounded-md border border-slate-200 px-2 text-xs"
                                value={selectedSection.paragraphFontFamily || 'global'}
                                onChange={(event) => updateSelectedSection('paragraphFontFamily', event.target.value)}
                              >
                                <option value="global">Use global ({BUILDER_FONTS.find(font => font.value === paragraphFontFamily)?.label || 'Inter'})</option>
                                {BUILDER_FONTS.map(font => <option key={font.label} value={font.value}>{font.label}</option>)}
                              </select>
                            </div>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs">Variant</Label>
                            <select value={(() => {
                              const variants = getAccessibleSectionLibrary().find(item => item.type === selectedSection.type)?.variants || [];
                              return variants.includes(selectedSection.variant) ? selectedSection.variant : (variants[0] || '');
                            })()} onChange={(event) => updateSelectedSection('variant', event.target.value)} className="h-9 w-full rounded-md border border-slate-200 px-2 text-xs focus:border-blue-500 focus:outline-none" data-builder-field="variant" data-testid="builder-variant-select">
                              {(getAccessibleSectionLibrary().find(item => item.type === selectedSection.type)?.variants || []).map(variant => <option key={variant}>{variant}</option>)}
                            </select>
                          </div>
                          {!['footer', 'trust'].includes(selectedSection.type) && (
                            <div className="space-y-1.5">
                              <Label className="text-xs">Heading</Label>
                              <Input value={selectedSection.heading || ''} onChange={(event) => updateSelectedSection('heading', event.target.value)} className="text-xs" data-builder-field="heading" data-testid="builder-heading-input" />
                            </div>
                          )}
                          <div className="space-y-1.5">
                            <Label className="text-xs">Description & Content</Label>
                            <RichTextEditor
                              value={selectedSection.subtext || ''}
                              onChange={(val) => updateSelectedSection('subtext', val)}
                              placeholder="Write detailed section features, content..."
                              minHeight="160px"
                            />
                          </div>
                          {['hero', 'cta'].includes(selectedSection.type) && (
                            <div className="space-y-1.5">
                              <Label className="text-xs">Button Text</Label>
                              <Input value={selectedSection.primaryText || ''} onChange={(event) => updateSelectedSection('primaryText', event.target.value)} className="text-xs" data-builder-field="primaryText" data-testid="builder-cta-input" />
                            </div>
                          )}
                          <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                            <span className="text-xs font-medium text-slate-700">Visible on storefront</span>
                            <input type="checkbox" checked={Boolean(selectedSection.visible)} onChange={(event) => updateSelectedSection('visible', event.target.checked)} data-builder-field="visible" className="h-4 w-4 accent-blue-600" />
                          </label>
                        </>
                      ) : <p className="text-xs text-slate-400">Select a section in the canvas to edit it.</p>}
                    </CardContent>
                  </Card>}
                </div>
              <Dialog open={Boolean(canvasRichTextEdit)} onOpenChange={open => {
                if (!open) setCanvasRichTextEdit(null);
              }}>
                <DialogContent className="max-w-4xl">
                  <DialogHeader>
                    <DialogTitle>Rich text editor — {canvasRichTextEdit?.label}</DialogTitle>
                  </DialogHeader>
                  {canvasRichTextEdit && (
                    <RichTextEditor
                      value={canvasRichTextEdit.value}
                      onChange={value => {
                        const section = pageSections.find(item => item.id === canvasRichTextEdit.sectionId);
                        if (!section) return;
                        if (canvasRichTextEdit.cardIndex !== null) {
                          updateSharedCardContent(
                            section.type,
                            canvasRichTextEdit.cardIndex,
                            canvasRichTextEdit.field,
                            value,
                            canvasRichTextEdit.listIndex
                          );
                        } else {
                          updateSharedSectionContent(section.type, { [canvasRichTextEdit.field]: value });
                        }
                        setCanvasRichTextEdit(current => current ? { ...current, value } : current);
                      }}
                      minHeight="280px"
                    />
                  )}
                  <div className="mt-4 flex justify-end">
                    <Button type="button" onClick={() => setCanvasRichTextEdit(null)}>Done</Button>
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            )}

            {/* ── DOMAIN SETUP TAB ─────────────────────────────────────────── */}
            {activeTab === 'domain' && (
              <div className="space-y-6 max-w-3xl">
                {/* Active domain banner */}
                {isDomainLive && (
                  <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-4 flex items-center justify-between"
                    data-testid="domain-active-banner">
                    <div>
                      <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        {settings.domain_type === 'subdomain'
                          ? `${settings.subdomain}.cloudsetu.com`
                          : settings.custom_domain} — Active
                      </p>
                      <p className="text-xs text-green-600 mt-0.5">
                        Your white-label domain is live. Clients will see your branding.
                      </p>
                    </div>
                    <a
                      href={settings.domain_type === 'subdomain'
                        ? `/public/${settings.subdomain}`
                        : `https://${settings.custom_domain}/public/${settings.subdomain || ''}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs text-green-700 font-medium hover:underline"
                      data-testid="wl-preview-link">
                      <ExternalLink className="h-3.5 w-3.5" /> Preview Storefront
                    </a>
                  </div>
                )}

                {isDomainPending && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 text-sm text-amber-800 font-medium flex items-center gap-2"
                    data-testid="domain-pending-banner">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    DNS verification pending for <b>{settings.custom_domain}</b>. Add the CNAME records below and click Verify DNS.
                  </div>
                )}

                {/* 3 Option cards */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-100">
                    <h2 className="font-semibold text-slate-800">Domain Setup</h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Choose how your clients access your white-label portal
                    </p>
                  </div>

                  {/* Option selector */}
                  <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { key: 'subdomain', label: 'Free Subdomain', desc: 'mycompany.cloudsetu.com', badge: 'Free', color: 'blue' },
                      { key: 'custom', label: 'Your Own Domain', desc: 'mycompany.com', badge: 'Advanced', color: 'violet' },
                      { key: 'buy', label: 'Buy a Domain', desc: 'Search & purchase new', badge: 'Premium', color: 'amber' },
                    ].map(opt => (
                      <button key={opt.key}
                        onClick={() => resetDomainState(opt.key)}
                        data-testid={`wl-option-${opt.key}`}
                        className={`relative rounded-xl border-2 p-4 text-left transition-all ${domainOption === opt.key
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
                        <span className={`absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full
                          ${opt.color === 'blue' ? 'bg-blue-100 text-blue-700'
                            : opt.color === 'violet' ? 'bg-violet-100 text-violet-700'
                              : 'bg-amber-100 text-amber-700'}`}>
                          {opt.badge}
                        </span>
                        <p className="font-semibold text-slate-800 text-sm">{opt.label}</p>
                        <p className="text-xs text-slate-500 mt-0.5 font-mono">{opt.desc}</p>
                      </button>
                    ))}
                  </div>

                  {/* Option: Subdomain */}
                  {domainOption === 'subdomain' && (
                    <div className="px-6 pb-6 space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1.5">
                          Enter your subdomain name
                        </label>
                        <div className="flex gap-2">
                          <div className="flex flex-1 rounded-lg border border-slate-200 overflow-hidden">
                            <input value={domainInput}
                              onChange={e => setDomainInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                              placeholder="mycompany"
                              className="flex-1 px-3 py-2.5 text-sm outline-none"
                              data-testid="wl-subdomain-input" />
                            <span className="px-3 py-2.5 bg-slate-50 border-l border-slate-200 text-sm text-slate-500">
                              .cloudsetu.com
                            </span>
                          </div>
                          <Button onClick={handleSetSubdomain} disabled={domainLoading || !domainInput}
                            className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="wl-subdomain-submit">
                            {domainLoading ? 'Saving…' : 'Activate'}
                          </Button>
                        </div>
                        {domainInput && (
                          <p className="text-xs text-slate-400 mt-1">
                            Preview: <b>{domainInput}.cloudsetu.com</b>
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Option: Custom Domain */}
                  {domainOption === 'custom' && (
                    <div className="px-6 pb-6 space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1.5">
                          Enter your domain
                        </label>
                        <div className="flex gap-2">
                          <input value={domainInput}
                            onChange={e => setDomainInput(e.target.value.toLowerCase())}
                            placeholder="mycompany.com"
                            className="flex-1 h-10 border border-slate-200 rounded-lg px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                            data-testid="wl-custom-domain-input" />
                          <Button onClick={handleSetCustomDomain} disabled={domainLoading || !domainInput}
                            className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="wl-custom-domain-submit">
                            {domainLoading ? 'Saving…' : 'Get DNS Records'}
                          </Button>
                        </div>
                      </div>

                      {/* DNS Records Table */}
                      {(dnsRecords || isDomainPending) && (
                        <div className="rounded-xl border border-slate-200 overflow-hidden">
                          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                            <p className="text-xs font-semibold text-slate-700">DNS Records to Add in Your Registrar</p>
                            <Button size="sm" variant="outline" onClick={handleVerifyDNS} disabled={verifying}
                              className="text-xs h-7" data-testid="wl-verify-dns-btn">
                              {verifying ? 'Checking…' : 'Verify DNS'}
                            </Button>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-slate-100">
                                  {['Type', 'Name', 'Value', 'TTL'].map(h => (
                                    <th key={h} className="text-left px-4 py-2 text-slate-500 font-medium">{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(dnsRecords || [
                                  { type: 'CNAME', name: '@', value: 'connectdesk.cloudsetu.com', ttl: '3600' },
                                  { type: 'CNAME', name: 'www', value: 'connectdesk.cloudsetu.com', ttl: '3600' },
                                ]).map((rec, i) => (
                                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                                    <td className="px-4 py-2.5 font-mono font-bold text-violet-700">{rec.type}</td>
                                    <td className="px-4 py-2.5 font-mono">{rec.name}</td>
                                    <td className="px-4 py-2.5 font-mono text-blue-700 max-w-[220px] truncate">{rec.value}</td>
                                    <td className="px-4 py-2.5 text-slate-400">{rec.ttl}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <p className="px-4 py-2.5 text-[11px] text-slate-400">
                            DNS propagation can take 5 min – 48 hrs. Click "Verify DNS" after adding the records.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Option: Buy Domain */}
                  {domainOption === 'buy' && (
                    <div className="px-6 pb-6 space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1.5">
                          Search for a domain
                        </label>
                        <div className="flex gap-2">
                          <input value={domainInput}
                            onChange={e => setDomainInput(e.target.value.toLowerCase())}
                            placeholder="mycompany"
                            className="flex-1 h-10 border border-slate-200 rounded-lg px-3 text-sm outline-none focus:ring-2 focus:ring-amber-500"
                            onKeyDown={e => e.key === 'Enter' && handleSearchDomains()}
                            data-testid="wl-domain-search-input" />
                          <Button onClick={handleSearchDomains} disabled={searching || !domainInput}
                            className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="wl-domain-search-btn">
                            {searching ? 'Searching…' : 'Search'}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-400 mt-1.5">
                          Note: Domain search requires production environment. May not work in preview.
                        </p>
                      </div>
                      {searchResults && (
                        <div className="space-y-2">
                          {searchResults.length === 0
                            ? <p className="text-sm text-slate-500">No results found.</p>
                            : searchResults.map((d, i) => (
                              <div key={i}
                                className={`flex items-center justify-between rounded-lg border px-4 py-3 ${d.available ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50 opacity-60'
                                  }`}
                                data-testid={`domain-result-${i}`}>
                                <div>
                                  <p className="text-sm font-mono font-semibold text-slate-800">{d.domainName}</p>
                                  <p className={`text-xs mt-0.5 ${d.available ? 'text-green-600' : 'text-slate-400'}`}>
                                    {d.available ? 'Available' : 'Unavailable'}
                                  </p>
                                </div>
                                {d.available && (
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-bold text-slate-800">
                                      ₹{Number(d.price || 0).toLocaleString('en-IN')}/yr
                                    </span>
                                    <Button size="sm" onClick={() => handleBuyDomain(d.domainName, d.price)}
                                      disabled={domainLoading}
                                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 gap-1"
                                      data-testid={`buy-domain-btn-${i}`}>
                                      <ShoppingCart className="w-3 h-3" />
                                      Request
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))
                          }
                        </div>
                      )}
                    </div>
                  )}

                  {/* Domain message */}
                  {domainMsg && (
                    <div className={`mx-6 mb-5 rounded-lg px-4 py-3 text-sm ${domainMsg.type === 'success'
                      ? 'bg-green-50 border border-green-200 text-green-800'
                      : domainMsg.type === 'error'
                        ? 'bg-red-50 border border-red-200 text-red-700'
                        : 'bg-blue-50 border border-blue-200 text-blue-800'
                      }`} data-testid="wl-domain-msg">
                      {domainMsg.text}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── SSL SETUP TAB ─────────────────────────────────────────────── */}
            {activeTab === 'ssl' && (
              <div className="max-w-3xl space-y-6">
                {/* Current domain status */}
                {isDomainLive && settings?.domain_type !== 'subdomain' ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-4 flex items-center gap-3"
                    data-testid="ssl-domain-verified">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-green-800">
                        DNS Verified: {settings.custom_domain}
                      </p>
                      <p className="text-xs text-green-600 mt-0.5">
                        Your domain is pointing correctly to CloudSetu. You can now request SSL.
                      </p>
                    </div>
                  </div>
                ) : settings?.domain_type === 'subdomain' ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-4 flex items-center gap-3">
                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-blue-800">
                        Using CloudSetu Subdomain ({settings.subdomain}.cloudsetu.com)
                      </p>
                      <p className="text-xs text-blue-600 mt-0.5">
                        Subdomains are automatically covered by CloudSetu's wildcard SSL. No action needed.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">No Domain Configured Yet</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Please set up your domain in the "Domain Setup" tab first before requesting SSL.
                      </p>
                    </div>
                  </div>
                )}

                {/* SSL Instructions */}
                <Card data-testid="ssl-instructions-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-blue-600" />
                      SSL Certificate Setup Instructions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <p className="text-sm text-slate-600">
                      SSL certificates for custom domains are managed manually by the CloudSetu admin team.
                      Follow the steps below to get HTTPS enabled for your custom domain.
                    </p>

                    <ol className="space-y-4">
                      {[
                        {
                          step: '1',
                          title: 'Configure Your Custom Domain',
                          desc: 'Go to the "Domain Setup" tab and enter your custom domain. Click "Get DNS Records" to receive the CNAME records.',
                          color: 'blue',
                        },
                        {
                          step: '2',
                          title: 'Add DNS Records at Your Registrar',
                          desc: 'Log into your domain registrar (GoDaddy, Namecheap, Cloudflare, etc.) and add the CNAME records provided. DNS changes can take 5 minutes to 48 hours.',
                          color: 'violet',
                        },
                        {
                          step: '3',
                          title: 'Verify DNS',
                          desc: 'Once DNS has propagated, go back to "Domain Setup" and click "Verify DNS". This confirms your domain is pointing to CloudSetu.',
                          color: 'cyan',
                        },
                        {
                          step: '4',
                          title: 'Request SSL Certificate',
                          desc: 'After DNS is verified, contact the CloudSetu admin team via the Support section and raise a ticket: "SSL Certificate Request for [yourdomain.com]". Admin will provision the certificate within 24 hours.',
                          color: 'green',
                          highlight: true,
                        },
                        {
                          step: '5',
                          title: 'HTTPS Activated',
                          desc: 'Once SSL is issued, your domain will serve over HTTPS automatically. Your clients will see a secure padlock in their browser.',
                          color: 'emerald',
                        },
                      ].map((item) => (
                        <li key={item.step}
                          className={`flex gap-4 p-4 rounded-xl ${item.highlight ? 'bg-blue-50 border border-blue-100' : 'bg-slate-50'}`}>
                          <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white
                            ${item.color === 'blue' ? 'bg-blue-500'
                              : item.color === 'violet' ? 'bg-violet-500'
                                : item.color === 'cyan' ? 'bg-cyan-500'
                                  : item.color === 'green' ? 'bg-green-500'
                                    : 'bg-emerald-500'}`}>
                            {item.step}
                          </span>
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{item.title}</p>
                            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{item.desc}</p>
                          </div>
                        </li>
                      ))}
                    </ol>

                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <p className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                        <Info className="h-3.5 w-3.5" /> Important Notes
                      </p>
                      <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
                        <li>SSL certificates are free for all verified partners.</li>
                        <li>Wildcard SSL is available for *.cloudsetu.com subdomains automatically.</li>
                        <li>Custom domain SSL is manually issued — plan at least 24 hours in advance.</li>
                        <li>Domains purchased via "Buy a Domain" get SSL configured automatically.</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </div>

      {/* Domain Request Modal */}
      <DomainRequestModal
        open={modalOpen}
        onClose={() => !modalSubmitting && setModalOpen(false)}
        domain={pendingDomain}
        years={1}
        price={pendingPrice}
        isEmailOnly={false}
        onSubmit={handleModalSubmit}
        submitting={modalSubmitting}
      />

      {/* Publish Section Audit Warning */}
      <Dialog
        open={publishAuditState.open}
        onOpenChange={open => !open && setPublishAuditState({ open: false, missingSections: [] })}
      >
        <DialogContent className="sm:max-w-md" data-testid="publish-audit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              Sections Missing from Live Site
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            The following sections from the Admin-approved template are not in your current page. Your customers won't see them on the live site:
          </p>
          <ul className="mt-2 space-y-1.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            {publishAuditState.missingSections.map(name => (
              <li key={name} className="flex items-center gap-2 text-sm font-medium text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                {name}
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500">
            Ask Admin to restore these sections, or publish anyway if they are intentionally removed.
          </p>
          <div className="flex gap-3 pt-1">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              data-testid="publish-audit-cancel"
              onClick={() => setPublishAuditState({ open: false, missingSections: [] })}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="flex-1 border-0 bg-amber-600 text-white hover:bg-amber-700"
              data-testid="publish-audit-confirm"
              disabled={builderSaving}
              onClick={() => {
                setPublishAuditState({ open: false, missingSections: [] });
                savePageBuilderState(true);
              }}
            >
              {builderSaving ? 'Publishing…' : 'Publish Anyway'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
